import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  ArrowLeft,
  Edit, 
  Trash2, 
  User,
  Mail,
  Phone,
  Building2,
  FolderOpen,
  FileText,
  Truck,
  Package,
  Save,
  X,
  Calendar,
  MapPin,
  DollarSign,
  Weight,
  AlertCircle,
  Eye,
  Download,
  Search,
  Filter,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  Send,
  QrCode
} from 'lucide-react';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import autoTable from 'jspdf-autotable';
import { Row, Col, Button, Form, Card, Badge, Modal, Tab, Nav, Table, Spinner, Alert, InputGroup, Dropdown, Toast, ToastContainer } from 'react-bootstrap';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import { sendEmailService } from '../services/emailService';
import {
  validateFilesForEmail,
  filesToEmailAttachmentPayloads,
  EMAIL_ATTACHMENT_MAX_FILES,
  EMAIL_ATTACHMENT_MAX_TOTAL_BYTES
} from '../utils/emailClientAttachments';
import { useUser } from '../contexts/UserContext';
import './Dashboard.css';
import './ProjectDetails.css';
import { deliveriesLoadStatusBadgeProps, loadStatusDisplayAsStored } from '../constants/logisticsDisplay';

// Helper to display empty string instead of "N/A" for missing values
const displayValue = (val) => (val === null || val === undefined ? '' : val);

// Helper to render email as mailto link
const renderEmailLink = (email) => {
  if (!email || email.trim() === '') return ' ';
  const emailValue = email.trim();
  return (
    <a 
      href={`mailto:${emailValue}`} 
      style={{ color: '#0d6efd', textDecoration: 'none' }}
      onClick={(e) => e.stopPropagation()}
    >
      {emailValue}
    </a>
  );
};

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  const debounced = (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
  debounced.cancel = () => clearTimeout(timeout);
  return debounced;
};


// Helper function to convert LoadDesignator to 2-letter code (AA, BB, CC, etc.)
const convertLoadDesignatorToLetters = (loadDesignator) => {
  if (!loadDesignator && loadDesignator !== 0) return '';
  
  try {
    const raw = parseFloat(loadDesignator);
    if (isNaN(raw)) return '';
    
    // Format as "00.00" with leading zeros (e.g., 1.15 becomes "01.15")
    // This matches C# ToString("00.00", CultureInfo.InvariantCulture)
    const formatted = raw.toFixed(2);
    const parts = formatted.split('.');
    let left = parts.length > 0 ? parts[0] : '00';
    const right = parts.length > 1 ? parts[1] : '00';
    
    // Pad left part to at least 2 digits (e.g., "1" becomes "01")
    left = left.padStart(2, '0');
    
    // Get last 2 characters of left part
    const last2 = left.slice(-2);
    
    const n1 = parseInt(last2, 10);
    const n2 = parseInt(right, 10);
    
    if (isNaN(n1) || isNaN(n2)) return '';
    
    const c1 = String.fromCharCode(n1 + 65);
    const c2 = String.fromCharCode(n2 + 65);
    
    return `${c1}${c2}`;
  } catch (error) {
    console.error('Error converting loadDesignator:', loadDesignator, error);
    return '';
  }
};

// LoadDetailsSection Component
const LoadDetailsSection = ({ load, releases, companyId, projectId, onReleaseClick, onNavigateToRelease, project, user }) => {
  const navigate = useNavigate();
  const { user: userFromContext } = useUser();
  const currentUser = user || userFromContext;
  const [loadDetails, setLoadDetails] = useState({
    fabricationAlias: '',
    longestBar: '',
    dropSite: '',
    submittedBy: '',
    submittedByPhone: '',
    totalWeight: 0,
    shippedDate: null
  });
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [qrModalShow, setQrModalShow] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [qrPlainText, setQrPlainText] = useState('');
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState(null);
  const [qrTrackUrl, setQrTrackUrl] = useState('');

  // Fetch additional load details
  useEffect(() => {
    const fetchLoadDetails = async () => {
      if (!load?.id) return;
      
      setLoadingDetails(true);
      try {
        // Fetch fabrication alias, longest bar, drop site, submitted by, total weight, in-transit date
        const response = await apiService.get(`/api/projects/load/${load.id}/details`);
        if (response.success && response.data) {
          setLoadDetails(response.data);
        }
      } catch (error) {
        console.error('Error fetching load details:', error);
      } finally {
        setLoadingDetails(false);
      }
    };
    
    fetchLoadDetails();
  }, [load?.id]);

  /**
   * Parse Streamline/SQL datetime strings without treating `YYYY-MM-DD` as UTC midnight.
   * Browser `new Date('2016-05-24')` is UTC-based and can shift calendar day/time in local TZ.
   */
  const parseStreamlineDateTime = (value) => {
    if (value == null || value === '') return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value === 'number' && Number.isFinite(value)) {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    const s = String(value).trim();
    if (!s) return null;

    // ISO datetime with Z, but Streamline commonly stores "calendar" timestamps that should not shift by TZ.
    // Example: `2016-05-24T00:00:00.000Z` should display as local midnight on 2016-05-24 (not +offset).
    const zulu = s.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?Z$/
    );
    if (zulu) {
      const y = Number(zulu[1]);
      const mo = Number(zulu[2]);
      const da = Number(zulu[3]);
      const hh = Number(zulu[4]);
      const mm = Number(zulu[5]);
      const ss = Number(zulu[6]);
      let ms = 0;
      if (zulu[7] != null) {
        const frac = String(zulu[7]);
        const padded = `${frac}000`.slice(0, 3);
        ms = Number(padded);
      }
      if (!Number.isFinite(ms)) ms = 0;
      const d = new Date(y, mo - 1, da, hh, mm, ss, ms);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    // ISO-ish with explicit offset (non-Zulu) — let JS handle it.
    if (/[+-]\d{2}:?\d{2}$/.test(s)) {
      const d = new Date(s);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    // `YYYY-MM-DD` or `YYYY-MM-DD HH:mm:ss(.fff)?` or `YYYY-MM-DDTHH:mm:ss(.fff)?` (no timezone suffix)
    const m = s.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?)?$/
    );
    if (m) {
      const y = Number(m[1]);
      const mo = Number(m[2]);
      const da = Number(m[3]);
      const hh = m[4] != null ? Number(m[4]) : 0;
      const mm = m[5] != null ? Number(m[5]) : 0;
      const ss = m[6] != null ? Number(m[6]) : 0;
      let ms = 0;
      if (m[7] != null) {
        const frac = String(m[7]);
        const padded = `${frac}000`.slice(0, 3);
        ms = Number(padded);
      }
      if (!Number.isFinite(ms)) ms = 0;
      const d = new Date(y, mo - 1, da, hh, mm, ss, ms);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const d = parseStreamlineDateTime(dateString);
      if (!d) return '';
      return d.toLocaleDateString();
    } catch {
      return '';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    try {
      const d = parseStreamlineDateTime(dateString);
      if (!d) return '';
      return d.toLocaleString();
    } catch {
      return '';
    }
  };

  const convertInchesToFeetAndInches = (inches) => {
    if (!inches || isNaN(inches)) return '';
    const totalInches = parseFloat(inches);
    const feet = Math.floor(totalInches / 12);
    const remainingInches = Math.round(totalInches % 12);
    // Format as 00'-00" with leading zeros
    const feetFormatted = String(feet).padStart(2, '0');
    const inchesFormatted = String(remainingInches).padStart(2, '0');
    return `${feetFormatted}'-${inchesFormatted}"`;
  };

  const getBOLNumber = () => {
    const loadNo = load.loadNo || load.loadNumber || '';
    return loadNo ? `B${loadNo}` : '';
  };

  const getLoadCode = () => {
    return convertLoadDesignatorToLetters(load.loadDesignator);
  };

  const getStatusBadgeForLoad = (status) => {
    if (!status) return <Badge bg="secondary">Unknown</Badge>;
    const sl = String(status).toLowerCase();
    const display = loadStatusDisplayAsStored(status);
    if (sl === 'pending') {
      return (
        <Badge bg="warning" text="dark">
          {display}
        </Badge>
      );
    }
    if (sl === 'cancelled') {
      return <Badge bg="danger">{display}</Badge>;
    }
    return <Badge {...deliveriesLoadStatusBadgeProps(status)}>{display}</Badge>;
  };

  // Calculate total weight from releases
  const totalWeight = releases.reduce((sum, release) => sum + (parseFloat(release.weight) || 0), 0);
  const shippedDate = loadDetails.shippedDate || load.loadDate;

  // Check if user is a customer: user's company has isCustomer = 1 AND matches project's customerCompany
  const userCompanyId = currentUser?.assetId || currentUser?.company?.id;
  const userCompanyIsCustomer = currentUser?.company?.isCustomer === 1;
  const isCustomer = userCompanyIsCustomer && project?.companyId && userCompanyId && 
    String(project.companyId).trim() === String(userCompanyId).trim();
  
  // Check if user is a placer: user's company has isPlacer = 1 AND matches project's placerCompany
  const userCompanyIsPlacer = currentUser?.company?.isPlacer === 1;
  const isUserPlacer = userCompanyIsPlacer && project?.placerCompany && userCompanyId && (
    String(project.placerCompany).trim() === String(userCompanyId).trim() ||
    project.placerCompany.toString().split(',').map(c => c.trim()).includes(String(userCompanyId).trim())
  );
  
  // Check if ContractType is NOT 'Unit Price per Lb'
  // Hide weight only if user is customer (not placer) AND ContractType is NOT 'Unit Price per Lb'
  const contractType = project?.contractType || '';
  const shouldHideWeight = isCustomer && !isUserPlacer && contractType !== 'Unit Price per Lb';

  // Same as before enriched display: branch alias (Assets) or raw FabricationLocation on the load
  const fabricationPrimaryLabel =
    loadDetails.fabricationAlias || displayValue(load.fabricationLocation);

  const buildLoadQrPayload = (trackUrl = '') => {
    const projAddr = project
      ? [project.projectAddress, project.projectCity, project.projectState, project.projectZip].filter(Boolean).join(', ')
      : '';
    const lines = [
      `Bill of Lading: B${load.loadNo || load.loadNumber || ''}`,
      `Status: ${load.status ? loadStatusDisplayAsStored(load.status) : ''}`,
      load.loadDesignator != null && load.loadDesignator !== ''
        ? `Designator: ${load.loadDesignator}`
        : null,
      `Ship via: ${displayValue(load.shipVia)}`,
      `Trailer: ${displayValue(load.trailerNo)}`,
      `Fabrication: ${loadDetails.fabricationAlias || displayValue(load.fabricationLocation)}`,
      `Drop site: ${loadDetails.dropSite || ''}`,
      `Delivery Date: ${formatDateTime(shippedDate)}`,
      project?.custProjName || project?.projectName ? `Project: ${project.custProjName || project.projectName}` : null,
      projAddr ? `Project address: ${projAddr}` : null,
      trackUrl ? `Driver track URL: ${trackUrl}` : null
    ].filter(Boolean);
    return lines.join('\n');
  };

  const openLoadQrModal = async () => {
    setQrError(null);
    setQrLoading(true);
    try {
      let trackUrl = '';
      if (currentUser?.role === 'superAdmin') {
        const loadNoForIssue = (load.loadNo || load.loadNumber || '').toString().trim();
        const res = await apiService.issueLogisticsTokenAdmin(
          loadNoForIssue
            ? { loadNo: loadNoForIssue, externalRef: `project-details-${load.id}` }
            : { loadId: load.id, externalRef: `project-details-${load.id}` }
        );
        if (res.success && res.data?.qrUrl) {
          trackUrl = res.data.qrUrl;
        } else {
          throw new Error(res.message || 'Could not issue driver link');
        }
      }
      const encodeStr = buildLoadQrPayload(trackUrl);
      setQrPlainText(encodeStr);
      setQrTrackUrl(trackUrl);
      const dataUrl = await QRCode.toDataURL(encodeStr, { width: 280, margin: 2, errorCorrectionLevel: 'M' });
      setQrDataUrl(dataUrl);
      setQrModalShow(true);
    } catch (e) {
      setQrError(e.message || 'Failed to build QR');
      setQrDataUrl('');
      setQrPlainText('');
      setQrTrackUrl('');
      setQrModalShow(true);
    } finally {
      setQrLoading(false);
    }
  };

  const downloadLoadQr = () => {
    if (!qrDataUrl) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = `bol-B${load.loadNo || load.loadNumber || load.id}-qr.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="load-details-section">
      {/* Enhanced Header */}
      <div className="load-header-section">
        <div className="d-flex align-items-center mb-4">
          <div className="status-indicator me-3">
            <div className="status-dot"></div>
          </div>
          <div className="flex-grow-1">
            <div className="d-flex align-items-center mb-2">
              <span className="load-title mb-0" style={{ fontWeight: 'normal' }}>
                Bill of Lading #: <span style={{ fontWeight: 'bold', color: '#000' }}>B{load.loadNo || load.loadNumber || ''}</span>
              </span>
              <div className="ms-auto d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-link p-0 text-secondary"
                  title={
                    currentUser?.role === 'superAdmin'
                      ? 'QR with load details and driver track link (issues new token)'
                      : 'QR with load details'
                  }
                  disabled={qrLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    openLoadQrModal();
                  }}
                  aria-label="Load QR code"
                >
                  {qrLoading ? <Spinner animation="border" size="sm" /> : <QrCode size={22} strokeWidth={1.75} />}
                </button>
                {getStatusBadgeForLoad(load.status)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Details Grid */}
      <div className="load-details-grid mb-4">
        <div className="detail-section">
          <div className="detail-item">
            <span className="detail-label">Submitted by:</span>
            <span className="detail-value">
              {loadDetails.submittedBy || ' '}
              {loadDetails.submittedByPhone && ` (${loadDetails.submittedByPhone})`}
            </span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Delivery by:</span>
            <span className="detail-value">{displayValue(load.shipVia)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Fabrication:</span>
            <span className="detail-value">{fabricationPrimaryLabel}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Longest bar:</span>
            <span className="detail-value">{loadDetails.longestBar ? convertInchesToFeetAndInches(loadDetails.longestBar) : ' '}</span>
          </div>
        </div>
        <div className="detail-section">
          <div className="detail-item">
            <span className="detail-label">Delivery Date:</span>
            <span className="detail-value">{formatDateTime(shippedDate)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Trailer #:</span>
            <span className="detail-value">{displayValue(load.trailerNo)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Drop site:</span>
            <span className="detail-value">{loadDetails.dropSite || ' '}</span>
          </div>
          {!shouldHideWeight && (
            <div className="detail-item">
              <span className="detail-label">Weight:</span>
              <span className="detail-value">{totalWeight > 0 ? `${Math.round(totalWeight)} lbs` : displayValue(load.loadDesignator)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Enhanced Releases Section */}
      <div className="releases-section">
        <div className="releases-header">
          <h6 className="releases-title">Releases</h6>
          <span className="releases-count">{releases.length} items</span>
        </div>
        
        {releases.length === 0 ? (
          <div className="no-releases">
            <p className="text-muted mb-0">
              <span className="me-2">📦</span>
              No releases found for this load.
            </p>
          </div>
        ) : (
          <div className="releases-list">
            {releases.map((release, index) => (
              <div 
                key={release.id} 
                className={`release-item ${index % 2 === 0 ? 'even' : 'odd'}`}
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  // Navigate to releases tab and expand the release (without modal)
                  if (onNavigateToRelease) {
                    onNavigateToRelease(release.id);
                  } else if (onReleaseClick) {
                    onReleaseClick(release);
                  }
                }}
              >
                <span className="release-number">
                  {release.releaseNumber || `Release ${release.release || ''}`}
                </span>
                <span className="release-description">
                  {release.description || ''}
                </span>
                {!shouldHideWeight && release.weight > 0 && (
                  <span className="release-weight">
                    ({Math.round(release.weight)} lbs)
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal show={qrModalShow} onHide={() => setQrModalShow(false)} centered size="md">
        <Modal.Header closeButton>
          <Modal.Title>Load QR</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {qrError && <Alert variant="danger">{qrError}</Alert>}
          {currentUser?.role === 'superAdmin' && qrTrackUrl && (
            <p className="small text-muted mb-2">
              A new driver link was issued. Older QR links for this load are no longer valid.
            </p>
          )}
          {qrDataUrl && (
            <div className="text-center mb-3">
              <img src={qrDataUrl} alt="Load QR" className="img-fluid" style={{ maxWidth: 280 }} />
            </div>
          )}
          {qrPlainText && (
            <Form.Group>
              <Form.Label className="small">Encoded text</Form.Label>
              <div
                className="small font-monospace form-control"
                style={{ height: 'auto', minHeight: 220, whiteSpace: 'pre-wrap', overflowY: 'auto' }}
              >
                {qrPlainText.split('\n').map((line, idx) => {
                  if (line.startsWith('Driver track URL: ') && qrTrackUrl) {
                    return (
                      <div key={idx}>
                        Driver track URL:{' '}
                        <a href={qrTrackUrl} target="_blank" rel="noopener noreferrer">
                          {qrTrackUrl}
                        </a>
                      </div>
                    );
                  }
                  return <div key={idx}>{line || ' '}</div>;
                })}
              </div>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setQrModalShow(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={downloadLoadQr} disabled={!qrDataUrl}>
            <Download size={16} className="me-1" />
            Download PNG
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

// ReleaseDetailsCard Component
const ReleaseDetailsCard = ({ release, onReleaseUpdate, loads = [], companyId, projectId, onSwitchToLoads, placerCompany, project }) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [inputType, setInputType] = useState('weight'); // 'percentage' or 'weight'
  const [updateWeight, setUpdateWeight] = useState(release.placedWt || '');
  // Get load for initial date value
  const initialLoad = release.loadId ? loads.find(l => l.id === release.loadId) : null;
  const [updateDate, setUpdateDate] = useState(() => {
    // Try to get placed date from load first, then from release
    // If no placed date exists, default to today's date (not delivery date or firmed date)
    const dateStr = initialLoad?.placedDate || release.placedDate || '';
    if (dateStr) {
      try {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD for date input
        }
      } catch (e) {
        // Invalid date, fall through to default
      }
    }
    // Default to today's date if no placed date exists
    return new Date().toISOString().split('T')[0];
  });

  // Update date when load's placedDate changes, but only if there's an actual placed date
  useEffect(() => {
    const currentLoad = release.loadId ? loads.find(l => l.id === release.loadId) : null;
    const dateStr = currentLoad?.placedDate || release.placedDate || '';
    if (dateStr) {
      try {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          setUpdateDate(date.toISOString().split('T')[0]);
        }
      } catch (e) {
        // Invalid date, keep current value
      }
    }
    // If no placed date, keep today's date (don't change it)
  }, [loads, release.loadId, release.placedDate]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  const showToastNotification = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return '';
    }
  };

  const getReleaseStatusBadge = (status) => {
    if (!status) return <Badge bg="secondary">Unknown</Badge>;

    const workflowProps = deliveriesLoadStatusBadgeProps(status);
    if (workflowProps.bg === null) {
      return (
        <Badge {...workflowProps}>
          {loadStatusDisplayAsStored(status)}
        </Badge>
      );
    }

    switch (status.toLowerCase()) {
      case 'placed':
        return <Badge bg="success">Placed</Badge>;
      case 'open':
        return <Badge bg="light" text="dark">Open</Badge>;
      case 'pending':
        return <Badge bg="warning" text="dark">Pending</Badge>;
      case 'detailing':
        return <Badge bg="light" text="dark">Detailing</Badge>;
      case 'returned':
        return <Badge bg="danger">Returned</Badge>;
      case 'review':
        return <Badge bg="info">Review</Badge>;
      default:
        return <Badge bg="light" text="dark">{status}</Badge>;
    }
  };

  const getReleaseProgress = (release) => {
    if (!release.weight || release.weight <= 0) return 0;
    if (!release.placedWt || release.placedWt <= 0) return 0;
    return Math.round((release.placedWt / release.weight) * 100);
  };

  // Get BOL number from LoadNo with "B" prefix
  const getBOLNumber = () => {
    if (!release.loadId) return '';
    const load = loads.find(l => l.id === release.loadId);
    if (load && load.loadNo) {
      return `B${load.loadNo}`;
    }
    return '';
  };

  // Handle BOL click to navigate to load
  const handleBOLClick = (e) => {
    e.stopPropagation();
    if (release.loadId && onSwitchToLoads) {
      onSwitchToLoads(release.loadId);
    }
  };

  const finalDate = release.placedDate || release.firmedDate || release.taggedDate || release.fabricatedDate || release.releaseDate;
  const statusBadge = getReleaseStatusBadge(release.status);
  const isPlacedStatus = release.status?.toLowerCase() === 'placed';
  const releaseStatusLower = release.status?.toLowerCase() ?? '';
  const isInTransitStatus =
    releaseStatusLower === 'intransit' || releaseStatusLower === 'shipped';
  
  // Check if user's company ID matches placer company
  const userCompanyId = user?.assetId || user?.company?.id;
  const userCompanyIsPlacer = user?.company?.isPlacer === 1;
  const isUserPlacer = userCompanyIsPlacer && placerCompany && userCompanyId && (
    String(placerCompany).trim() === String(userCompanyId).trim() ||
    placerCompany.toString().split(',').map(c => c.trim()).includes(String(userCompanyId).trim())
  );
  
  // Check if user is a customer
  const userCompanyIsCustomer = user?.company?.isCustomer === 1;
  const isCustomer = userCompanyIsCustomer && project?.companyId && userCompanyId && 
    String(project.companyId).trim() === String(userCompanyId).trim();
  
  // Check if should hide weight (customer + ContractType != 'Unit Price per Lb')
  const contractType = project?.contractType || '';
  const shouldHideWeightForCustomer = isCustomer && contractType !== 'Unit Price per Lb';
  
  // Check IncludePAsInCost and SacCode logic
  const includePAsInCost = project?.includePAsInCost === 1;
  const sacCode = release.sacCode || '';
  const isPA = sacCode === 'PA';
  
  // Determine if Update Weight section and 100% Placed button should be shown
  // If IncludePAsInCost = 1, show weight and button
  // If IncludePAsInCost = 0:
  //   - If SacCode = 'PA', hide Update Weight line and 100% Placed button
  //   - If SacCode != 'PA', show weight and 100% Placed button
  const shouldShowUpdateWeight = includePAsInCost || !isPA;
  const shouldShow100PercentButton = includePAsInCost || !isPA;
  
  const canUpdateWeight = (isPlacedStatus || isInTransitStatus) && isUserPlacer && shouldShowUpdateWeight; // Show update only if user is placer and conditions are met
  const progressPercent = isPlacedStatus ? getReleaseProgress(release) : 0;
  
  // Get load information for delivery date and placed date
  const load = release.loadId ? loads.find(l => l.id === release.loadId) : null;
  const deliveryDate = load?.deliveryDate || null;
  const loadPlacedDate = load?.placedDate || release.placedDate || deliveryDate || null;

  // Handle weight update
  const handleUpdateWeight = async (e) => {
    e.stopPropagation();
    if (isUpdating) return;
    
    if (!updateWeight || parseFloat(updateWeight) < 0) {
      showToastNotification('Please enter a valid value.', 'error');
      return;
    }
    
    // Calculate placed weight based on input type
    let calculatedWeight;
    if (inputType === 'percentage') {
      if (!release.weight || release.weight <= 0) {
        showToastNotification('Total weight is required to calculate percentage.', 'error');
        return;
      }
      const percentage = parseFloat(updateWeight);
      if (percentage < 0 || percentage > 100) {
        showToastNotification('Percentage must be between 0 and 100.', 'error');
        return;
      }
      calculatedWeight = (percentage / 100) * release.weight;
    } else {
      calculatedWeight = parseFloat(updateWeight);
    }
    
    // Round to whole number (no decimal places)
    calculatedWeight = Math.round(calculatedWeight);
    
    setIsUpdating(true);
    try {
      console.log('Updating weight:', calculatedWeight, 'Date:', updateDate);
      
      // Date is already in YYYY-MM-DD format from date input
      let formattedDate = updateDate;
      
      await apiService.updateReleaseWeight(release.id, {
        placedWt: calculatedWeight,
        placedDate: formattedDate || new Date().toISOString().split('T')[0]
      });
      
      // Update the local release data
      const updatedRelease = {
        ...release,
        placedWt: calculatedWeight,
        placedDate: formattedDate,
        status: 'Placed'
      };
      
      // Notify parent component to update the releases state
      if (onReleaseUpdate) {
        onReleaseUpdate(updatedRelease);
      }
      
      const displayValue = inputType === 'percentage' 
        ? `${updateWeight}% (${calculatedWeight} lbs)`
        : `${calculatedWeight} lbs`;
      
      showToastNotification(`Weight updated successfully! Weight Placed: ${displayValue}, Date: ${updateDate}`, 'success');
      
    } catch (error) {
      console.error('Error updating weight:', error);
      showToastNotification('Error updating weight. Please try again.', 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className={`release-details-card ${canUpdateWeight ? 'placed-status' : ''}`}>
      {/* Header Section */}
      <div className="release-card-header">
        <div className="release-header-left">
          <div className="release-status-indicator"></div>
          <div>
            <h5 className="release-card-title">
              {release.description || `Release #: ${release.releaseNumber || release.release || ''}`}
            </h5>
            <p className="release-card-subtitle text-muted">
              {release.description ? `Release #: ${release.releaseNumber || release.release || ''}` : 'Release Details'}
            </p>
          </div>
        </div>
        <div className="release-header-right" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          {statusBadge}
          <p className="release-card-date">{formatDate(finalDate)}</p>
        </div>
      </div>

      {/* Progress Bar - Only for Placed status */}
      {isPlacedStatus && release.weight > 0 && !shouldHideWeightForCustomer && (
        <div className="release-progress-section">
          <div className="release-progress-bar">
            <div 
              className="release-progress-fill" 
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
          <span className="release-progress-percentage">{progressPercent}%</span>
        </div>
      )}

      {/* Details Grid */}
      <div className="release-details-grid">
        <div className="release-detail-section">
          <div className="release-detail-item">
            <span className="release-detail-label">BOL:</span>
            {getBOLNumber() ? (
              <span 
                className="release-detail-value"
                style={{ 
                  cursor: 'pointer',
                  color: '#0d6efd',
                  textDecoration: 'underline'
                }}
                onClick={handleBOLClick}
                title="Click to view load details"
              >
                {displayValue(getBOLNumber())}
              </span>
            ) : (
              <span className="release-detail-value">{displayValue(getBOLNumber())}</span>
            )}
          </div>
          <div className="release-detail-item">
            <span className="release-detail-label">Delivery date:</span>
            <span className="release-detail-value">{formatDate(deliveryDate)}</span>
          </div>
          {!shouldHideWeightForCustomer && (
            <div className="release-detail-item">
              <span className="release-detail-label">Total Weight:</span>
              <span className="release-detail-value">
                {release.weight ? `${release.weight} lbs.` : ''}
              </span>
            </div>
          )}
        </div>
        <div className="release-detail-section">
          <div className="release-detail-item">
            <span className="release-detail-label">Released by:</span>
            <span className="release-detail-value">
              {release.releasedByUser ?
                `${release.releasedByUser.firstName || ''} ${release.releasedByUser.lastName || ''}`.trim() || 'Unknown User' :
                displayValue(release.releasedBy)
              }
              {release.releasedByUser?.phone && (
                <span className="ms-2 text-muted">({release.releasedByUser.phone})</span>
              )}
            </span>
          </div>
          <div className="release-detail-item">
            <span className="release-detail-label">Placed date:</span>
            <span className="release-detail-value">
              {loadPlacedDate ? formatDate(loadPlacedDate) : <span className="text-muted">Not set</span>}
            </span>
          </div>
          {!shouldHideWeightForCustomer && (
            <div className="release-detail-item">
              <span className="release-detail-label">Weight Placed:</span>
              <span className="release-detail-value">
                {release.placedWt ? `${release.placedWt} lbs.` : '0 lbs.'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Update Weight Section - Only for Placed and In Transit status */}
      {canUpdateWeight && (
        <div className="release-update-section">
          <div className="release-update-form">
            {/* Radio buttons for input type */}
            <div className="d-flex align-items-center gap-3 me-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name={`weightType-${release.id}`}
                  id={`weight-${release.id}`}
                  value="weight"
                  checked={inputType === 'weight'}
                  onChange={(e) => {
                    setInputType('weight');
                    setUpdateWeight('');
                  }}
                />
                <label className="form-check-label" htmlFor={`weight-${release.id}`}>
                  Weight
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name={`weightType-${release.id}`}
                  id={`percentage-${release.id}`}
                  value="percentage"
                  checked={inputType === 'percentage'}
                  onChange={(e) => {
                    setInputType('percentage');
                    setUpdateWeight('');
                  }}
                />
                <label className="form-check-label" htmlFor={`percentage-${release.id}`}>
                  %
                </label>
              </div>
            </div>
            <input
              type="number"
              className="release-weight-input"
              placeholder={inputType === 'percentage' ? 'Percentage (0-100)' : 'Weight placed (lbs)'}
              value={updateWeight}
              onChange={(e) => setUpdateWeight(e.target.value)}
              step={inputType === 'percentage' ? '0.01' : '0.01'}
              min="0"
              max={inputType === 'percentage' ? '100' : undefined}
            />
            <input
              type="date"
              className="release-date-input"
              value={updateDate}
              onChange={(e) => {
                e.stopPropagation();
                setUpdateDate(e.target.value);
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              onFocus={(e) => {
                e.stopPropagation();
              }}
              min={(() => {
                if (deliveryDate) {
                  try {
                    const date = new Date(deliveryDate);
                    if (!isNaN(date.getTime())) {
                      return date.toISOString().split('T')[0];
                    }
                  } catch (e) {
                    // Ignore errors
                  }
                }
                return undefined;
              })()}
              style={{ cursor: 'pointer' }}
            />
            <button
              className={`release-update-btn ${isUpdating ? 'updating' : ''}`}
              onClick={handleUpdateWeight}
              disabled={isUpdating}
            >
              {isUpdating ? 'Updating...' : 'Update Weight'}
            </button>
          </div>
        </div>
      )}
      
      <ToastContainer position="top-end" className="p-3">
        <Toast show={showToast} onClose={() => setShowToast(false)} delay={4000} autohide className="shadow">
          <Toast.Header closeButton={false} className="bg-white">
            {toastType === 'success' ? (
              <CheckCircle className="text-success me-2" size={20} />
            ) : (
              <AlertCircle className="text-danger me-2" size={20} />
            )}
            <strong className="me-auto">
              {toastType === 'success' ? 'Success' : 'Error'}
            </strong>
          </Toast.Header>
          <Toast.Body className={`bg-white ${toastType === 'success' ? 'text-success' : 'text-danger'}`}>
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

const ProjectDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { companyId, projectId } = useParams();
  const { user } = useUser();
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddSiteModal, setShowAddSiteModal] = useState(false);
  const [showReleaseDetailsModal, setShowReleaseDetailsModal] = useState(false);
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [showSendLocationModal, setShowSendLocationModal] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');
  
  // Email form state
  const [emailFormData, setEmailFormData] = useState({
    configurationId: '',
    receiver: '',
    cc: '',
    bcc: '',
    subject: '',
    body: ''
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [emailSuccess, setEmailSuccess] = useState(null);
  const [receiverQuery, setReceiverQuery] = useState('');
  const [receiverSuggestions, setReceiverSuggestions] = useState([]);
  const [showReceiverSuggestions, setShowReceiverSuggestions] = useState(false);
  const receiverInputRef = useRef(null);
  const receiverSuggestionsRef = useRef(null);
  const emailAttachmentInputRef = useRef(null);
  const [emailAttachments, setEmailAttachments] = useState([]);
  
  // Filter states
  const [releaseFilters, setReleaseFilters] = useState({
    search: '',
    status: 'all',
    dateFrom: '',
    dateTo: '',
    weightMin: '',
    weightMax: ''
  });
  const [tempFilters, setTempFilters] = useState({
    search: '',
    status: 'all',
    dateFrom: '',
    dateTo: '',
    weightMin: '',
    weightMax: ''
  });
  const [filteredReleases, setFilteredReleases] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Get project data from navigation state or set default
  const [project, setProject] = useState(location.state?.project || null);
  const [company, setCompany] = useState(location.state?.company || null);
  
  // Set activeTab from location.state if available (e.g., when coming from release report)
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);
  const [releases, setReleases] = useState([]);
  const [loads, setLoads] = useState([]);
  const [sites, setSites] = useState([]);
  const [releasesLoading, setReleasesLoading] = useState(false);
  const [loadsLoading, setLoadsLoading] = useState(false);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [releasesError, setReleasesError] = useState(null);
  const [loadsError, setLoadsError] = useState(null);
  const [sitesError, setSitesError] = useState(null);
  
  // State for expanded loads and their releases
  const [expandedLoads, setExpandedLoads] = useState(new Set());
  const [loadReleases, setLoadReleases] = useState({}); // Store releases for each load
  
  // State for expanded release details
  const [expandedReleases, setExpandedReleases] = useState(new Set());

  // Toast notification state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  const showToastNotification = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);
  };

  // State for editing site contact
  const [isEditingSiteContact, setIsEditingSiteContact] = useState(false);
  const [siteContactForm, setSiteContactForm] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSavingSiteContact, setIsSavingSiteContact] = useState(false);

  // State for editing company PM
  const [isEditingCompanyPM, setIsEditingCompanyPM] = useState(false);
  const [companyPMForm, setCompanyPMForm] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSavingCompanyPM, setIsSavingCompanyPM] = useState(false);

  const [editForm, setEditForm] = useState({
    projectName: '',
    projectNumber: '',
    custProjNo: '',
    tylerProjectName: '',
    tylerNumber: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    primarySite: '',
    summary: ''
  });

  const [siteForm, setSiteForm] = useState({
    siteAnnotation: '',
    description: '',
    latitude: '',
    longitude: '',
    orientation: '',
    siteContactName: '',
    siteContactPhone: '',
    siteContactEmail: '',
    addlMapInstructions: ''
  });

  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [mapSearchQuery, setMapSearchQuery] = useState('');
  const [mapSearchLoading, setMapSearchLoading] = useState(false);
  const [mapSearchError, setMapSearchError] = useState('');
  const [mapSearchSuggestions, setMapSearchSuggestions] = useState([]);
  const [mapSearchSuggestionsLoading, setMapSearchSuggestionsLoading] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [expandedSiteCards, setExpandedSiteCards] = useState(new Set());
  const [editingSite, setEditingSite] = useState(null);
  const sitesMapContainerRef = useRef(null);
  const sitesMapInstanceRef = useRef(null);
  const sitesMarkersRef = useRef([]);
  const addSiteMapRef = useRef(null);
  const addSiteMarkerRef = useRef(null);
  const mapSearchDebounceRef = useRef(null);

  // Scroll to top when component mounts or projectId changes
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [projectId, companyId]);

  // Fetch project data - always fetch fresh data on mount or when projectId changes
  useEffect(() => {
    if (projectId && companyId) {
      const fetchProjectData = async () => {
        try {
          setLoading(true);
          setError(null);
          
          const response = await apiService.getProjectById(projectId);
          
          if (response.success) {
            setProject(response.data);
            
            // Also fetch company data if not available
            if (!company) {
              const companyResponse = await apiService.getCompanyById(companyId);
              if (companyResponse.success) {
                setCompany(companyResponse.data);
              }
            }
          } else {
            setError(response.message || 'Failed to load project details');
          }
        } catch (error) {
          console.error('Error fetching project data:', error);
          setError('Failed to load project details');
        } finally {
          setLoading(false);
        }
      };

      fetchProjectData();
    }
  }, [projectId, companyId]); // Removed project and company from dependencies to always fetch fresh data

  // Initialize edit form with project data when project is available
  useEffect(() => {
    if (project) {
      setEditForm({
        projectName: project.projectName || '',
        projectNumber: project.projectNumber || '',
        custProjNo: project.custProjNo || '',
        tylerProjectName: project.tylerProjectName || '',
        tylerNumber: project.tylerNumber || '',
        contactName: project.contactName || '',
        contactEmail: project.contactEmail || '',
        contactPhone: project.contactPhone || '',
        primarySite: project.primarySite || '',
        summary: project.summary || ''
      });
    }
  }, [project]);

  // Fetch releases data
  const fetchReleases = async () => {
    if (!project?.id) return;
    
    try {
      setReleasesLoading(true);
      setReleasesError(null);
      const response = await apiService.getProjectReleases(project.id);
      
      if (response.success) {
        setReleases(response.data);
      }
    } catch (error) {
      console.error('Error fetching releases:', error);
      setReleasesError(error.message);
    } finally {
      setReleasesLoading(false);
    }
  };

  // Fetch loads data
  const fetchLoads = async () => {
    if (!project?.id) return;
    
    try {
      setLoadsLoading(true);
      setLoadsError(null);
      const response = await apiService.getProjectLoads(project.id);
      
      if (response.success) {
        setLoads(response.data);
      }
    } catch (error) {
      console.error('Error fetching loads:', error);
      setLoadsError(error.message);
    } finally {
      setLoadsLoading(false);
    }
  };

  // Fetch releases for a specific load
  const fetchLoadReleases = async (loadId) => {
    try {
      const response = await apiService.getReleasesByLoad(loadId);
      if (response.success) {
        setLoadReleases(prev => ({
          ...prev,
          [loadId]: response.data
        }));
      }
    } catch (error) {
      console.error('Error fetching load releases:', error);
    }
  };

  // Toggle load expansion - only one load open at a time
  const toggleLoadExpansion = async (loadId) => {
    const newExpandedLoads = new Set(expandedLoads);
    
    if (newExpandedLoads.has(loadId)) {
      // Collapse if already expanded
      newExpandedLoads.delete(loadId);
    } else {
      // Close all other loads and expand only this one
      newExpandedLoads.clear();
      newExpandedLoads.add(loadId);
      // Fetch releases if not already fetched
      if (!loadReleases[loadId]) {
        await fetchLoadReleases(loadId);
      }
    }
    
    setExpandedLoads(newExpandedLoads);
  };

  // Expand a specific load (used when navigating from links) - closes all others
  const expandLoadOnly = async (loadId) => {
    const newExpandedLoads = new Set();
    newExpandedLoads.add(loadId);
    
    // Fetch releases if not already fetched
    if (!loadReleases[loadId]) {
      await fetchLoadReleases(loadId);
    }
    
    setExpandedLoads(newExpandedLoads);
  };

  // Toggle release expansion - only one release open at a time
  const toggleReleaseExpansion = (releaseId) => {
    const newExpandedReleases = new Set(expandedReleases);
    
    if (newExpandedReleases.has(releaseId)) {
      // Collapse if already expanded
      newExpandedReleases.delete(releaseId);
    } else {
      // Close all other releases and expand only this one
      newExpandedReleases.clear();
      newExpandedReleases.add(releaseId);
    }
    
    setExpandedReleases(newExpandedReleases);
  };

  // Expand a specific release (used when navigating from links) - closes all others
  const expandReleaseOnly = (releaseId) => {
    const newExpandedReleases = new Set();
    newExpandedReleases.add(releaseId);
    setExpandedReleases(newExpandedReleases);
  };

  // Format date helper function
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return '';
    }
  };

  // Helper functions for release details (kept in sync with ReleaseDetailsCard header badge)
  const getReleaseStatusBadge = (status) => {
    if (!status) return <Badge bg="secondary">Unknown</Badge>;

    const workflowProps = deliveriesLoadStatusBadgeProps(status);
    if (workflowProps.bg === null) {
      return (
        <Badge {...workflowProps}>
          {loadStatusDisplayAsStored(status)}
        </Badge>
      );
    }

    switch (status.toLowerCase()) {
      case 'placed':
        return <Badge bg="success">Placed</Badge>;
      case 'open':
        return <Badge bg="light" text="dark">Open</Badge>;
      case 'pending':
        return <Badge bg="warning" text="dark">Pending</Badge>;
      case 'detailing':
        return <Badge bg="light" text="dark">Detailing</Badge>;
      case 'returned':
        return <Badge bg="danger">Returned</Badge>;
      case 'review':
        return <Badge bg="info">Review</Badge>;
      default:
        return <Badge bg="light" text="dark">{status}</Badge>;
    }
  };

  const getReleaseProgress = (release) => {
    if (!release.weight || release.weight <= 0) return 0;
    if (!release.placedWt || release.placedWt <= 0) return 0;
    return Math.round((release.placedWt / release.weight) * 100);
  };

  const formatReleaseDate = (release) => {
    // Prioritize placed date, then firmed date, then release date
    if (release.placedDate) return formatDate(release.placedDate);
    if (release.firmedDate) return formatDate(release.firmedDate);
    if (release.taggedDate) return formatDate(release.taggedDate);
    if (release.fabricatedDate) return formatDate(release.fabricatedDate);
    if (release.releaseDate) return formatDate(release.releaseDate);
    return '';
  };

  // Fetch sites data
  const fetchSites = async () => {
    if (!project?.id) return;
    
    try {
      setSitesLoading(true);
      setSitesError(null);
      const response = await apiService.getProjectSites(project.id);
      
      if (response.success) {
        setSites(response.data);
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
      setSitesError(error.message);
    } finally {
      setSitesLoading(false);
    }
  };

  useEffect(() => {
    // Force Poppins font on all elements in this component
    const style = document.createElement('style');
    style.textContent = `
      .project-details-wrapper *,
      .project-details-wrapper *::before,
      .project-details-wrapper *::after {
        font-family: 'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Clear mapError when map finishes loading (e.g., after fallback timeout)
  useEffect(() => {
    if (mapLoaded) {
      setMapError(false);
    }
  }, [mapLoaded]);

  // Debounced search suggestions for map search
  useEffect(() => {
    if (!mapSearchQuery || mapSearchQuery.trim().length < 3) {
      setMapSearchSuggestions([]);
      if (mapSearchDebounceRef.current) clearTimeout(mapSearchDebounceRef.current);
      return;
    }

    if (!mapLoaded) return;

    if (mapSearchDebounceRef.current) clearTimeout(mapSearchDebounceRef.current);

    mapSearchDebounceRef.current = setTimeout(async () => {
      setMapSearchSuggestionsLoading(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(mapSearchQuery)}&limit=5&addressdetails=1`
        );
        const results = await response.json();
        if (Array.isArray(results)) {
          setMapSearchSuggestions(results);
        } else {
          setMapSearchSuggestions([]);
        }
      } catch (err) {
        console.error('Suggestion search failed:', err);
        setMapSearchSuggestions([]);
      } finally {
        setMapSearchSuggestionsLoading(false);
      }
    }, 300);

    return () => {
      if (mapSearchDebounceRef.current) clearTimeout(mapSearchDebounceRef.current);
    };
  }, [mapSearchQuery, mapLoaded]);

  // Fetch releases, loads, and sites when project is available
  useEffect(() => {
    if (project?.id) {
      fetchReleases();
      fetchLoads();
      fetchSites();
    }
  }, [project?.id]);

  // Initialize filtered releases when releases change
  useEffect(() => {
    setFilteredReleases(releases);
    // Initialize temp filters to match current filters when releases change
    setTempFilters(releaseFilters);
  }, [releases]);

  // Initialize site contact form when project changes
  useEffect(() => {
    if (project?.siteContact) {
      setSiteContactForm({
        name: project.siteContact.name || '',
        email: project.siteContact.email || '',
        phone: project.siteContact.phone || ''
      });
    }
  }, [project?.siteContact]);

  // Initialize company PM form when project changes
  useEffect(() => {
    setCompanyPMForm({
      name: project?.custPMName || '',
      email: project?.custPMEmail || '',
      phone: project?.custPMPhone || ''
    });
  }, [project?.custPMName, project?.custPMEmail, project?.custPMPhone]);

  // Handle edit site contact
  const handleEditSiteContact = () => {
    setIsEditingSiteContact(true);
    setSiteContactForm({
      name: project?.siteContact?.name || '',
      email: project?.siteContact?.email || '',
      phone: project?.siteContact?.phone || ''
    });
  };

  // Handle save site contact
  const handleSaveSiteContact = async () => {
    try {
      setIsSavingSiteContact(true);
      const updateData = {
        siteContactName: siteContactForm.name,
        siteContactEmail: siteContactForm.email,
        siteContactPhone: siteContactForm.phone
      };

      const response = await apiService.updateProject(projectId, updateData);
      
      if (response.success) {
        // Refetch project data to get the latest from database
        const freshProjectResponse = await apiService.getProjectById(projectId);
        if (freshProjectResponse.success) {
          setProject(freshProjectResponse.data);
        } else {
          // Fallback: update local state with response data
          setProject(prev => ({
            ...prev,
            siteContact: response.data.siteContact || {
              name: siteContactForm.name,
              email: siteContactForm.email,
              phone: siteContactForm.phone
            }
          }));
        }
        setIsEditingSiteContact(false);
        showToastNotification('Site contact updated successfully', 'success');
      } else {
        showToastNotification('Failed to update site contact: ' + (response.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Error updating site contact:', error);
      showToastNotification('Failed to update site contact: ' + error.message, 'error');
    } finally {
      setIsSavingSiteContact(false);
    }
  };

  // Handle cancel editing site contact
  const handleCancelEditSiteContact = () => {
    setIsEditingSiteContact(false);
    setSiteContactForm({
      name: project?.siteContact?.name || '',
      email: project?.siteContact?.email || '',
      phone: project?.siteContact?.phone || ''
    });
  };

  // Handle edit company PM
  const handleEditCompanyPM = () => {
    setIsEditingCompanyPM(true);
    setCompanyPMForm({
      name: project?.custPMName || '',
      email: project?.custPMEmail || '',
      phone: project?.custPMPhone || ''
    });
  };

  // Handle save company PM
  const handleSaveCompanyPM = async () => {
    try {
      setIsSavingCompanyPM(true);
      const updateData = {
        custPMName: companyPMForm.name,
        custPMEmail: companyPMForm.email,
        custPMPhone: companyPMForm.phone
      };

      const response = await apiService.updateProject(projectId, updateData);
      
      if (response.success) {
        // Refetch project data to get the latest from database
        const freshProjectResponse = await apiService.getProjectById(projectId);
        if (freshProjectResponse.success) {
          setProject(freshProjectResponse.data);
        } else {
          // Fallback: update local state with response data
          setProject(prev => ({
            ...prev,
            custPMName: companyPMForm.name,
            custPMEmail: companyPMForm.email,
            custPMPhone: companyPMForm.phone
          }));
        }
        setIsEditingCompanyPM(false);
        showToastNotification('Company PM updated successfully', 'success');
      } else {
        showToastNotification('Failed to update company PM: ' + (response.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Error updating company PM:', error);
      showToastNotification('Failed to update company PM: ' + error.message, 'error');
    } finally {
      setIsSavingCompanyPM(false);
    }
  };

  // Handle cancel editing company PM
  const handleCancelEditCompanyPM = () => {
    setIsEditingCompanyPM(false);
    setCompanyPMForm({
      name: project?.custPMName || '',
      email: project?.custPMEmail || '',
      phone: project?.custPMPhone || ''
    });
  };

  // Load Leaflet Maps (more reliable than Google Maps)
  useEffect(() => {
    const loadLeafletMaps = () => {
      if (window.L) {
        setMapLoaded(true);
        setMapError(false);
        return;
      }

      // Check if script already exists
      if (document.querySelector('script[src*="leaflet"]')) {
        // Script exists, wait for it to load
        const checkLoaded = () => {
          if (window.L) {
            setMapLoaded(true);
          } else {
            setTimeout(checkLoaded, 100);
          }
        };
        checkLoaded();
        return;
      }

      // Load Leaflet CSS
      const cssLink = document.createElement('link');
      cssLink.rel = 'stylesheet';
      cssLink.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      cssLink.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
      cssLink.crossOrigin = '';
      document.head.appendChild(cssLink);

      // Load Leaflet JS
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
      script.crossOrigin = '';
      script.onload = () => {
        console.log('Leaflet Maps loaded successfully');
        setMapLoaded(true);
        setMapError(false);
      };
      script.onerror = (error) => {
        console.error('Failed to load Leaflet Maps:', error);
        setMapLoaded(false);
        setMapError(true);
      };
      
      document.head.appendChild(script);
    };

    loadLeafletMaps();
    
    // Set a timeout to show fallback if map doesn't load
    const timeout = setTimeout(() => {
      if (!mapLoaded) {
        setMapError(true);
      }
    }, 5000);
    
    return () => clearTimeout(timeout);
  }, []);

  const handleEditProject = () => {
    setShowEditModal(true);
  };

  const handleSaveProject = async () => {
    try {
      // Prepare data to send to API (only editable fields)
      const updateData = {
        projectName: editForm.projectName,
        custProjNo: editForm.custProjNo,
        contactName: editForm.contactName,
        contactEmail: editForm.contactEmail,
        contactPhone: editForm.contactPhone
      };

      const response = await apiService.updateProject(projectId, updateData);
      
      if (response.success) {
        // Refetch project data to get the latest from database
        const freshProjectResponse = await apiService.getProjectById(projectId);
        if (freshProjectResponse.success) {
          setProject(freshProjectResponse.data);
        } else {
          // Fallback: update local state with response data
          setProject(prev => ({
            ...prev,
            projectName: response.data.projectName,
            custProjNo: response.data.custProjNo,
            contactName: response.data.contactName,
            contactEmail: response.data.contactEmail,
            contactPhone: response.data.contactPhone
          }));
        }
        setShowEditModal(false);
        // Show success notification
        showToastNotification('Project updated successfully', 'success');
      } else {
        showToastNotification('Failed to update project: ' + (response.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Error updating project:', error);
      showToastNotification('Failed to update project: ' + error.message, 'error');
    }
  };

  const handleCancelEdit = () => {
    setEditForm({
      projectName: project.projectName,
      projectNumber: project.projectNumber,
      custProjNo: project.custProjNo,
      tylerProjectName: project.tylerProjectName,
      tylerNumber: project.tylerNumber,
      contactName: project.contactName,
      contactEmail: project.contactEmail,
      contactPhone: project.contactPhone,
      primarySite: project.primarySite,
      summary: project.summary
    });
    setShowEditModal(false);
  };

  // Filter releases based on current filters
  const applyFilters = (releasesToFilter, filtersToUse = releaseFilters) => {
    return releasesToFilter.filter(release => {
      // Search filter (release number, description)
      if (filtersToUse.search) {
        const searchTerm = filtersToUse.search.toLowerCase();
        const matchesSearch = 
          (release.releaseNumber && release.releaseNumber.toLowerCase().includes(searchTerm)) ||
          (release.description && release.description.toLowerCase().includes(searchTerm)) ||
          (release.acctCode && release.acctCode.toLowerCase().includes(searchTerm));
        if (!matchesSearch) return false;
      }

      // Status filter
      if (filtersToUse.status !== 'all') {
        if (release.status !== filtersToUse.status) return false;
      }

      // Date range filter
      if (filtersToUse.dateFrom) {
        const releaseDate = new Date(release.releaseDate);
        const fromDate = new Date(filtersToUse.dateFrom);
        if (releaseDate < fromDate) return false;
      }

      if (filtersToUse.dateTo) {
        const releaseDate = new Date(release.releaseDate);
        const toDate = new Date(filtersToUse.dateTo);
        if (releaseDate > toDate) return false;
      }

      // Weight range filter
      if (filtersToUse.weightMin) {
        const weight = parseFloat(release.weight) || 0;
        if (weight < parseFloat(filtersToUse.weightMin)) return false;
      }

      if (filtersToUse.weightMax) {
        const weight = parseFloat(release.weight) || 0;
        if (weight > parseFloat(filtersToUse.weightMax)) return false;
      }

      return true;
    });
  };

  // Update filters and apply them automatically
  const updateFilter = (filterName, value) => {
    const newTempFilters = { ...tempFilters, [filterName]: value };
    setTempFilters(newTempFilters);
    
    // Apply filters automatically
    setReleaseFilters(newTempFilters);
    const filtered = applyFilters(releases, newTempFilters);
    setFilteredReleases(filtered);
  };

  // Apply filters when button is clicked (re-applies current tempFilters)
  const applyFiltersToReleases = () => {
    setReleaseFilters(tempFilters);
    const filtered = applyFilters(releases, tempFilters);
    setFilteredReleases(filtered);
  };

  // Clear all filters
  const clearFilters = () => {
    const clearedFilters = {
      search: '',
      status: 'all',
      dateFrom: '',
      dateTo: '',
      weightMin: '',
      weightMax: ''
    };
    setReleaseFilters(clearedFilters);
    setTempFilters(clearedFilters);
    setFilteredReleases(releases);
  };

  // PDF export function for single release
  const exportSingleReleasePDF = (release) => {
    // Check if should hide weight (customer + ContractType != 'Unit Price per Lb')
    const userCompanyIdForPDF = user?.assetId || user?.company?.id;
    const userCompanyIsCustomerForPDF = user?.company?.isCustomer === 1;
    const isCustomerForPDF = userCompanyIsCustomerForPDF && project?.companyId && userCompanyIdForPDF && 
      String(project.companyId).trim() === String(userCompanyIdForPDF).trim();
    const contractTypeForPDF = project?.contractType || '';
    const shouldHideWeightForCustomerPDF = isCustomerForPDF && contractTypeForPDF !== 'Unit Price per Lb';
    
    const doc = new jsPDF();
    
    // Add header
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('Release Details', 20, 30);
    
    doc.setFontSize(16);
    doc.setFont(undefined, 'normal');
    doc.text(`Release Number: ${displayValue(release.releaseNumber)}`, 20, 45);
    
    // Add company/project info if available
    if (company) {
      doc.setFontSize(12);
      doc.text(`Company: ${company.name}`, 20, 55);
    }
    if (project) {
      doc.text(`Project: ${project.projectName || project.projectNumber || ''}`, 20, 60);
    }
    
    // Add line separator
    doc.line(20, 65, 190, 65);
    
    let yPosition = 80;
    
    // Basic Information section
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Basic Information', 20, yPosition);
    yPosition += 10;
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Description: ${release.description || ''}`, 20, yPosition);
    yPosition += 7;
    doc.text(`Status: ${release.status || ''}`, 20, yPosition);
    yPosition += 7;
    if (!shouldHideWeightForCustomerPDF) {
      doc.text(`Weight: ${release.weight || ''} lbs`, 20, yPosition);
      yPosition += 7;
    }
    yPosition += 15;
    
    // Project Information section
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Project Information', 20, yPosition);
    yPosition += 10;
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Project ID: ${release.projectId || ''}`, 20, yPosition);
    yPosition += 7;
    doc.text(`Load ID: ${release.loadId || ''}`, 20, yPosition);
    yPosition += 7;
    doc.text(`Bid Item: ${release.bidItem || ''}`, 20, yPosition);
    yPosition += 15;
    
    // Codes section
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Codes', 20, yPosition);
    yPosition += 10;
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Account Code: ${release.acctCode || ''}`, 20, yPosition);
    yPosition += 7;
    doc.text(`SAC Code: ${release.sacCode || ''}`, 20, yPosition);
    yPosition += 15;
    
    // Dates section
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Important Dates', 20, yPosition);
    yPosition += 10;
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Release Date: ${release.releaseDate ? new Date(release.releaseDate).toLocaleDateString() : ''}`, 20, yPosition);
    yPosition += 7;
    doc.text(`Firmed Date: ${release.firmedDate ? new Date(release.firmedDate).toLocaleDateString() : ''}`, 20, yPosition);
    yPosition += 7;
    doc.text(`Tagged Date: ${release.taggedDate ? new Date(release.taggedDate).toLocaleDateString() : ''}`, 20, yPosition);
    yPosition += 7;
    doc.text(`Fabricated Date: ${release.fabricatedDate ? new Date(release.fabricatedDate).toLocaleDateString() : ''}`, 20, yPosition);
    yPosition += 7;
    doc.text(`Placed Date: ${release.placedDate ? new Date(release.placedDate).toLocaleDateString() : ''}`, 20, yPosition);
    yPosition += 15;
    
    // Additional Information section
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Additional Information', 20, yPosition);
    yPosition += 10;
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Released By: ${release.releasedBy || ''}`, 20, yPosition);
    yPosition += 7;
    if (!shouldHideWeightForCustomerPDF) {
      doc.text(`Placed Weight: ${release.placedWt || ''} lbs`, 20, yPosition);
    }
    
    // Add footer
    doc.setFontSize(8);
    doc.text(`Generated on ${new Date().toLocaleString()}`, 20, 280);
    
    // Save the PDF
    doc.save(`Release_${release.releaseNumber || 'Details'}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // PDF export function for release listing
  const exportReleaseListingPDF = () => {
    const doc = new jsPDF();
    
    // Use filtered releases for export
    const releasesToExport = filteredReleases.length > 0 ? filteredReleases : releases;
    
    // Add header
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('Releases Report', 20, 30);
    
    // Add company/project info if available
    if (company) {
      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      doc.text(`Company: ${company.name}`, 20, 40);
    }
    if (project) {
      doc.text(`Project: ${project.projectName || project.projectNumber || ''}`, 20, 45);
    }
    
    // Add filter info if filters are active
    const hasActiveFilters = Object.values(releaseFilters).some(value => value && value !== 'all');
    if (hasActiveFilters) {
      doc.setFontSize(10);
      doc.text('Filtered Results', 20, 55);
    }
    
    // Add line separator
    doc.line(20, hasActiveFilters ? 60 : 50, 190, hasActiveFilters ? 60 : 50);
    
    // Prepare table data
    const tableData = releasesToExport.map(release => [
      release.releaseNumber || '',
      release.releaseDate ? new Date(release.releaseDate).toLocaleDateString() : '',
      release.description || '',
      release.status || '',
      release.weight ? `${release.weight} lbs` : '',
      release.acctCode || ''
    ]);
    
    // Add table
    autoTable(doc, {
      startY: 60,
      head: [['Release #', 'Release Date', 'Description', 'Status', 'Weight', 'Account Code']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      margin: { left: 20, right: 20 },
      styles: {
        fontSize: 9,
        cellPadding: 3
      }
    });
    
    // Add summary
    const finalY = 60 + (tableData.length * 8) + 30; // Approximate position after table
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('Summary', 20, finalY);
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Total Releases: ${releasesToExport.length}`, 20, finalY + 10);
    
    const totalWeight = releasesToExport.reduce((sum, release) => sum + (parseFloat(release.weight) || 0), 0);
    doc.text(`Total Weight: ${totalWeight.toFixed(2)} lbs`, 20, finalY + 17);
    
    // Add footer
    doc.setFontSize(8);
    doc.text(`Generated on ${new Date().toLocaleString()}`, 20, 280);
    
    // Save the PDF
    const fileName = `Project_Releases_${project?.projectNumber || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  const handleAddSite = () => {
    setShowAddSiteModal(true);
  };

  const toggleSiteCard = (siteId) => {
    setExpandedSiteCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(siteId)) {
        newSet.delete(siteId);
      } else {
        newSet.add(siteId);
      }
      return newSet;
    });
  };

  const handleEditSite = (site) => {
    // Pre-populate the site form with existing data
    setSiteForm({
      description: site.description || '',
      latitude: site.latitude || '',
      longitude: site.longitude || '',
      orientation: site.orientation || '',
      siteContactName: site.siteContactName || '',
      siteContactPhone: site.siteContactPhone || '',
      siteContactEmail: site.siteContactEmail || '',
      addlMapInstructions: site.addlMapInstructions || ''
    });

    // Set the selected location for the map
    if (site.latitude && site.longitude) {
      setSelectedLocation({
        lat: parseFloat(site.latitude),
        lng: parseFloat(site.longitude)
      });
    }

    setEditingSite(site);
    setShowAddSiteModal(true);
  };

  const handleCancelAddSite = () => {
    setShowAddSiteModal(false);
    setEditingSite(null);
    setSelectedLocation(null);
    setMapSearchQuery('');
    setMapSearchError('');
    setMapSearchSuggestions([]);

    // Clean up map
    if (addSiteMapRef.current) {
      addSiteMapRef.current.remove();
      addSiteMapRef.current = null;
    }
    addSiteMarkerRef.current = null;
    const mapElement = document.getElementById('site-map');
    if (mapElement) mapElement._map = null;

    setSiteForm({
      siteAnnotation: '',
      description: '',
      latitude: '',
      longitude: '',
      orientation: '',
      siteContactName: '',
      siteContactPhone: '',
      siteContactEmail: '',
      addlMapInstructions: ''
    });
  };

  const handleSaveSite = async () => {
    try {
      setLoading(true);

      let response;
      if (editingSite) {
        // Update existing site (exclude siteAnnotation to preserve original)
        const { siteAnnotation, ...updateData } = siteForm;
        response = await apiService.updateProjectSite(editingSite.id, updateData);
      } else {
        // Create new site
        response = await apiService.createProjectSite(project.id, siteForm);
      }

      if (response.success) {
        if (editingSite) {
          // Update the existing site in the sites list
          setSites(prev => prev.map(site =>
            site.id === editingSite.id ? response.data : site
          ));
          showToastNotification('Project site updated successfully!', 'success');
        } else {
          // Add the new site to the sites list
          setSites(prev => [...prev, response.data]);
          showToastNotification('Project site created successfully!', 'success');
        }

        setShowAddSiteModal(false);
        setEditingSite(null);
        setSelectedLocation(null);
        setMapSearchQuery('');
        setMapSearchError('');
        setMapSearchSuggestions([]);

        // Clean up map
        if (addSiteMapRef.current) {
          addSiteMapRef.current.remove();
          addSiteMapRef.current = null;
        }
        addSiteMarkerRef.current = null;
        const mapElement = document.getElementById('site-map');
        if (mapElement) mapElement._map = null;

        setSiteForm({
          siteAnnotation: '',
          description: '',
          latitude: '',
          longitude: '',
          orientation: '',
          siteContactName: '',
          siteContactPhone: '',
          siteContactEmail: '',
          addlMapInstructions: ''
        });
      }
    } catch (error) {
      console.error(`Error ${editingSite ? 'updating' : 'creating'} site:`, error);
      showToastNotification(`Failed to ${editingSite ? 'update' : 'create'} project site: ${error.message}`, 'error');
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSiteFormChange = (field, value) => {
    setSiteForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleReleaseUpdate = (updatedRelease) => {
    setReleases(prevReleases => 
      prevReleases.map(release => 
        release.id === updatedRelease.id ? updatedRelease : release
      )
    );
  };

  const updateAddSiteMarker = (lat, lng) => {
    if (!addSiteMapRef.current || !window.L) return;

    if (addSiteMarkerRef.current) {
      addSiteMarkerRef.current.setLatLng([lat, lng]);
    } else {
      addSiteMarkerRef.current = window.L.marker([lat, lng]).addTo(addSiteMapRef.current);
    }
    addSiteMarkerRef.current.bindPopup('Selected Location').openPopup();
  };

  const initializeMap = (mapElement) => {
    if (!mapLoaded || !mapElement || !window.L) return;

    // Clear any existing map
    mapElement.innerHTML = '';

    // Initialize Leaflet map
    const map = window.L.map(mapElement).setView([33.4484, -112.0740], 12); // Phoenix, AZ default
    addSiteMapRef.current = map;

    // Add OpenStreetMap tiles
    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);

    // Add click listener to map
    map.on('click', (event) => {
      const lat = event.latlng.lat;
      const lng = event.latlng.lng;
      
      // Update selected location
      setSelectedLocation({ lat, lng });
      
      // Update form fields
      setSiteForm(prev => ({
        ...prev,
        latitude: lat.toFixed(6),
        longitude: lng.toFixed(6)
      }));

      updateAddSiteMarker(lat, lng);
    });

    // Store map reference for cleanup
    mapElement._map = map;

    // If editing a site, center on existing location and place marker
    if (editingSite && selectedLocation) {
      map.setView([selectedLocation.lat, selectedLocation.lng], 15);
      setTimeout(() => {
        updateAddSiteMarker(selectedLocation.lat, selectedLocation.lng);
      }, 100);
    }
  };

  const applyMapSearchResult = (result) => {
    const { lat, lon, display_name: displayName } = result;
    const latNum = parseFloat(lat);
    const lonNum = parseFloat(lon);
    if (!Number.isFinite(latNum) || !Number.isFinite(lonNum)) return;

    setMapSearchQuery(displayName || `${latNum}, ${lonNum}`);
    setSelectedLocation({ lat: latNum, lng: lonNum });
    setSiteForm(prev => ({
      ...prev,
      latitude: latNum.toFixed(6),
      longitude: lonNum.toFixed(6)
    }));

    if (addSiteMapRef.current) {
      addSiteMapRef.current.setView([latNum, lonNum], 15);
    }

    updateAddSiteMarker(latNum, lonNum);

    if (addSiteMarkerRef.current && displayName) {
      addSiteMarkerRef.current.bindPopup(displayName).openPopup();
    }
  };

  // Initialize map when modal opens and map is loaded
  useEffect(() => {
    if (showAddSiteModal && mapLoaded && window.L) {
      // Add a small delay to ensure the modal DOM is fully rendered
      const timer = setTimeout(() => {
        const mapElement = document.getElementById('site-map');
        if (mapElement) {
          initializeMap(mapElement);
        }
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [showAddSiteModal, mapLoaded]);

  // Debounced autocomplete search
  const debouncedAutocompleteSearch = useCallback(
    debounce(async (query) => {
      if (!query.trim() || query.length < 2) {
        setMapSearchSuggestions([]);
        setMapSearchSuggestionsLoading(false);
        return;
      }

      try {
        const searchParams = new URLSearchParams({
          format: 'json',
          q: query,
          limit: 6,
          'accept-language': navigator.language || 'en',
          addressdetails: 1
        });

        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?${searchParams.toString()}`,
          {
            headers: {
              'User-Agent': 'Streamline-Tyler-Reinforcing/1.0'
            }
          }
        );

        const results = await response.json();
        setMapSearchSuggestions(Array.isArray(results) ? results : []);
      } catch (error) {
        console.error('Autocomplete search failed:', error);
        setMapSearchSuggestions([]);
      } finally {
        setMapSearchSuggestionsLoading(false);
      }
    }, 300), // 300ms debounce
    []
  );

  // Effect for autocomplete search
  useEffect(() => {
    if (mapSearchQuery) {
      setMapSearchSuggestionsLoading(true);
      debouncedAutocompleteSearch(mapSearchQuery);
    } else {
      setMapSearchSuggestions([]);
    }

    return () => debouncedAutocompleteSearch.cancel?.();
  }, [mapSearchQuery, debouncedAutocompleteSearch]);

  // Load search history from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('mapSearchHistory');
    if (saved) {
      try {
        setSearchHistory(JSON.parse(saved));
      } catch (error) {
        console.error('Error loading search history:', error);
      }
    }
  }, []);

  // Save search to history
  const saveSearchToHistory = (query, result) => {
    const searchItem = {
      query: query.trim(),
      result,
      timestamp: Date.now(),
      id: Date.now() + Math.random()
    };

    const updatedHistory = [searchItem, ...searchHistory.filter(item => item.query !== query)].slice(0, 10);
    setSearchHistory(updatedHistory);
    localStorage.setItem('mapSearchHistory', JSON.stringify(updatedHistory));
  };

  // Clear search history
  const clearSearchHistory = () => {
    setSearchHistory([]);
    localStorage.removeItem('mapSearchHistory');
  };

  // Geolocation search for "near me" functionality
  const handleGeolocationSearch = () => {
    if (!navigator.geolocation) {
      setMapSearchError('Geolocation is not supported by this browser');
      return;
    }

    setMapSearchLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          // Reverse geocode to get address
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
            {
              headers: {
                'User-Agent': 'Streamline-Tyler-Reinforcing/1.0'
              }
            }
          );
          const result = await response.json();

          if (result) {
            applyMapSearchResult(result);
            setMapSearchQuery(result.display_name || `${latitude}, ${longitude}`);
          }
        } catch (error) {
          console.error('Reverse geocoding failed:', error);
          setMapSearchError('Unable to find your location details');
        } finally {
          setMapSearchLoading(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        let errorMessage = 'Unable to get your location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please enable location permissions.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
        }
        setMapSearchError(errorMessage);
        setMapSearchLoading(false);
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  };

  const handleMapSearch = async () => {
    if (!mapLoaded) {
      setMapSearchError('Map is still loading. Please wait a moment.');
      return;
    }
    const query = mapSearchQuery.trim();
    if (!query) {
      setMapSearchError('Enter an address or place to search.');
      return;
    }

    setMapSearchLoading(true);
    setMapSearchError('');

    try {
      // Enhanced Nominatim API call with better parameters
      const searchParams = new URLSearchParams({
        format: 'json',
        q: query,
        limit: 8, // Increased for more options
        countrycodes: '', // Can be set to limit to specific countries
        'accept-language': navigator.language || 'en',
        addressdetails: 1, // Get detailed address info
        extratags: 1, // Get additional tags
        namedetails: 1 // Get alternative names
      });

      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?${searchParams.toString()}`,
        {
          headers: {
            'User-Agent': 'Streamline-Tyler-Reinforcing/1.0' // Required by Nominatim
          }
        }
      );
      const results = await response.json();

      if (!Array.isArray(results) || results.length === 0) {
        setMapSearchError('No locations found. Try a different search.');
        return;
      }

      applyMapSearchResult(results[0]);
      // Save to search history
      saveSearchToHistory(query, results[0]);
      // Keep latest results for quick re-select
      setMapSearchSuggestions(results);
    } catch (error) {
      console.error('Map search failed:', error);
      setMapSearchError('Search failed. Please try again.');
    } finally {
      setMapSearchLoading(false);
    }
  };

  // Initialize the main sites map in the Sites tab
  const initializeSitesMap = () => {
    if (!mapLoaded || !window.L || !sitesMapContainerRef.current) return;

    // If map already exists, skip init
    if (sitesMapInstanceRef.current) return;

    const fallbackCenter = [33.4484, -112.0740]; // Phoenix default
    const initialSite = sites.find(s => s.latitude && s.longitude);
    const center = initialSite ? [initialSite.latitude, initialSite.longitude] : fallbackCenter;

    const map = window.L.map(sitesMapContainerRef.current).setView(center, 13);
    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);

    sitesMapInstanceRef.current = map;
  };

  // Update markers whenever sites change
  const createMarkerIcon = (label, isSelected, isProjectSite) => {
    // Use red colors for project sites, blue for others
    const baseColor = isProjectSite ? '#dc2626' : '#0ea5e9';
    const selectedColor = isProjectSite ? '#b91c1c' : '#2563eb';
    const borderColor = isProjectSite ? '#991b1b' : '#38bdf8';
    const selectedBorderColor = isProjectSite ? '#7f1d1d' : '#1d4ed8';

    return window.L.divIcon({
      className: 'site-marker-icon',
      html: `
        <div style="
          background:${isSelected ? selectedColor : baseColor};
          color:white;
          width:32px;
          height:32px;
          border-radius:16px;
          display:flex;
          align-items:center;
          justify-content:center;
          font-weight:700;
          box-shadow: 0 2px 6px rgba(0,0,0,0.35);
          border: ${isSelected ? `3px solid ${selectedBorderColor}` : `2px solid ${borderColor}`};
        ">${label || ''}</div>
      `,
      iconSize: [32, 32],
      iconAnchor: [16, 32]
    });
  };

  const refreshSiteMarkers = () => {
    if (!sitesMapInstanceRef.current || !window.L) return;

    // Clear old markers
    sitesMarkersRef.current.forEach(m => m.remove());
    sitesMarkersRef.current = [];

    if (!sites.length) return;

    const map = sitesMapInstanceRef.current;
    const bounds = window.L.latLngBounds([]);

    sites.forEach(site => {
      if (site.latitude && site.longitude) {
        const label = site.siteAnnotation || '';
        // Check if this is a project site (case insensitive search in description or annotation, or annotation is null/empty)
        const isProjectSite = (site.description && site.description.toLowerCase().includes('project site')) ||
                             (site.siteAnnotation && site.siteAnnotation.toLowerCase().includes('project site')) ||
                             (site.siteAnnotation === null || site.siteAnnotation === '' || site.siteAnnotation === undefined);



        const marker = window.L.marker([site.latitude, site.longitude], {
          title: `Site ${label}${isProjectSite ? ' (Project Site)' : ''}`,
          icon: createMarkerIcon(label, selectedSiteId === site.id, isProjectSite)
        }).addTo(map);
        marker.bindPopup(
          `<div><strong>Site ${label || ''}</strong><br/>${site.description || ''}<br/>Lat: ${site.latitude}<br/>Lng: ${site.longitude}</div>`
        );
        marker.on('click', () => {
          setSelectedSiteId(site.id);
          marker.openPopup();
        });
        sitesMarkersRef.current.push(marker);
        bounds.extend([site.latitude, site.longitude]);
      }
    });

    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  };

  // When selected site changes, refresh marker icons and focus map/popup
  useEffect(() => {
    if (!selectedSiteId || !sitesMapInstanceRef.current) {
      refreshSiteMarkers();
      return;
    }
    refreshSiteMarkers();
    const map = sitesMapInstanceRef.current;
    const targetSite = sites.find(s => s.id === selectedSiteId && s.latitude && s.longitude);
    if (targetSite) {
      map.flyTo([targetSite.latitude, targetSite.longitude], Math.max(map.getZoom(), 14), { duration: 0.5 });
      // open popup for selected marker
      const marker = sitesMarkersRef.current.find(m => {
        const ll = m.getLatLng();
        return ll && ll.lat === targetSite.latitude && ll.lng === targetSite.longitude;
      });
      if (marker) marker.openPopup();
    }
  }, [selectedSiteId, sites]);

  // Handle editing site - place marker on existing location
  useEffect(() => {
    if (editingSite && selectedLocation && showAddSiteModal) {
      // Update form with coordinates when editing
      setSiteForm(prev => ({
        ...prev,
        latitude: selectedLocation.lat.toString(),
        longitude: selectedLocation.lng.toString()
      }));
    }
  }, [editingSite, selectedLocation, showAddSiteModal]);

  useEffect(() => {
    if (activeTab === 'sites') {
      initializeSitesMap();
      refreshSiteMarkers();
    }
  }, [activeTab, mapLoaded, sites]);

  // Pre-populate email form when site is selected
  useEffect(() => {
    if (selectedSite && showSendLocationModal) {
      const siteName = selectedSite.siteAnnotation ? `Site ${selectedSite.siteAnnotation}` : 'Project site';
      const mapLink = selectedSite.latitude && selectedSite.longitude 
        ? `https://www.google.com/maps?q=${selectedSite.latitude},${selectedSite.longitude}&z=15`
        : '';
      
      // Build subject according to format:
      // If customer number exists: "%custProjNo% (TRLLC: %tylerNumber%) %custProjName% - Site location for Site %siteAnnotation%: %description%"
      // If no customer number: "%tylerNumber% %custProjName% - Site location for Site %siteAnnotation%: %description%"
      let subject = '';
      const custProjNo = project?.custProjNo || '';
      const tylerNumber = project?.tylerNumber || '';
      const projectName = project?.custProjName || 'Project';
      const siteAnnotation = selectedSite.siteAnnotation || '';
      const siteDescription = selectedSite.description || '';
      
      if (custProjNo) {
        subject = `${custProjNo} (TRLLC: ${tylerNumber}) ${projectName} - Site location for Site ${siteAnnotation}: ${siteDescription}`;
      } else if (tylerNumber) {
        subject = `${tylerNumber} ${projectName} - Site location for Site ${siteAnnotation}: ${siteDescription}`;
      } else {
        subject = `${projectName} - Site location for Site ${siteAnnotation}: ${siteDescription}`;
      }
      
      let body = `<h3>Site Location Information</h3>`;
      body += `<p><strong>Project:</strong> ${project?.custProjName || 'N/A'}</p>`;
      body += `<p><strong>Site:</strong> ${siteName}</p>`;
      if (selectedSite.description) {
        body += `<p><strong>Description:</strong> ${selectedSite.description}</p>`;
      }
      if (selectedSite.latitude && selectedSite.longitude) {
        body += `<p><strong>Coordinates:</strong><br>`;
        body += `Latitude: ${selectedSite.latitude.toFixed(6)}<br>`;
        body += `Longitude: ${selectedSite.longitude.toFixed(6)}</p>`;
        body += `<p><strong>Map Link:</strong> <a href="${mapLink}" target="_blank">View on Google Maps</a></p>`;
      }
      body += `<p>Please find the site location details above.</p>`;

      setEmailAttachments([]);
      setEmailFormData(prev => ({
        ...prev,
        configurationId: '', // Use default configuration
        subject: subject,
        body: body
      }));
    }
  }, [selectedSite, showSendLocationModal, project]);

  // Handle click outside to close receiver suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        receiverSuggestionsRef.current &&
        !receiverSuggestionsRef.current.contains(event.target) &&
        receiverInputRef.current &&
        !receiverInputRef.current.contains(event.target)
      ) {
        setShowReceiverSuggestions(false);
      }
    };

    if (showSendLocationModal) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSendLocationModal]);

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'invoiced':
        return <Badge bg="success">{status}</Badge>;
      case 'closed':
        return <Badge bg="secondary">{status}</Badge>;
      case 'sold':
        return <Badge bg="primary">{status}</Badge>;
      case 'final':
        return <Badge bg="info">{status}</Badge>;
      case 'lost':
        return <Badge bg="danger">{status}</Badge>;
      case 'no bid':
        return <Badge bg="warning" text="dark">{status}</Badge>;
      case 'retail-dead':
        return <Badge bg="dark">{status}</Badge>;
      case 'retail w/ detailing':
        return <Badge bg="warning" text="dark">{status}</Badge>;
      case 'not built':
        return <Badge bg="outline-secondary">{status}</Badge>;
      default:
        return (
          <Badge bg="light" text="dark">
            {status ? loadStatusDisplayAsStored(status) : 'Unknown'}
          </Badge>
        );
    }
  };

  /** Loads.Status — In Transit shown as In Transit (see logisticsDisplay). */
  const renderLoadWorkflowStatusBadge = (status) => (
    <Badge {...deliveriesLoadStatusBadgeProps(status)}>{loadStatusDisplayAsStored(status)}</Badge>
  );

  return (
    <div className="project-details-wrapper">
      <div className="modern-dashboard">

        {/* No Project Data - Only show if not loading and no project data available */}
        {!project && !loading && !location.state?.project && (
          <div className="text-center py-5">
            <Alert variant="warning">
              <AlertCircle size={24} className="mb-3" />
              <h4>No Project Data Available</h4>
              <p>Please navigate to this page from the projects list.</p>
              <Button 
                variant="primary" 
                onClick={() => navigate(`/company/${companyId}/projects`)}
              >
                Back to Projects
              </Button>
            </Alert>
          </div>
        )}

        {/* Project Details */}
        {project && (
          <>
            {/* Header Section */}
            <div className="dashboard-header-section mb-4">
              <div className="d-flex justify-content-between align-items-center">
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center mb-2">
                    <Button
                      variant="link"
                      className="p-0 me-3 text-decoration-none text-dark"
                      style={{ color: '#1f2937' }}
                      onClick={() => {
                        // If tylerNumber starts with 'R', go to retailers page, otherwise go to projects page
                        const targetPath = project.tylerNumber && project.tylerNumber.startsWith('R')
                          ? `/company/${companyId}/retailers`
                          : `/company/${companyId}/projects`;
                        navigate(targetPath);
                      }}
                    >
                      <ArrowLeft size={20} />
                    </Button>
                    <div>
                      <h1 className="dashboard-title mb-1">{project.custProjName || 'Project'}</h1>
                      {project.custProjNo || project.tylerProjectName || project.tylerNumber ? (
                        <small className="dashboard-subtitle d-block">
                          {project.custProjNo ? `Project #${project.custProjNo}` : ''}
                          {project.custProjNo && (project.tylerProjectName || project.tylerNumber) ? ' | ' : ''}
                          {project.tylerProjectName ? `Tyler: ${project.tylerProjectName}` : ''}
                          {project.tylerNumber ? ` (#${project.tylerNumber})` : ''}
                        </small>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <Button variant="outline-primary" size="sm" onClick={() => setShowEditModal(true)}>
                    <Edit size={14} className="me-1" />
                    <span className="d-none d-sm-inline">Edit</span>
                  </Button>
                </div>
              </div>
            </div>

        {/* Compact Horizontal Tabs */}
        <div className="mb-3 compact-tabs">
          <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="border-0">
            <Nav.Item onClick={() => setActiveTab('summary')} className="clickable-tab">
              <Nav.Link eventKey="summary" className="d-flex align-items-center py-2 px-3">
                <FileText size={14} className="me-1" />
                <span className="small">Summary</span>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item onClick={() => setActiveTab('releases')} className="clickable-tab">
              <Nav.Link eventKey="releases" className="d-flex align-items-center py-2 px-3">
                <Package size={14} className="me-1" />
                <span className="small">Releases</span>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item onClick={() => setActiveTab('loads')} className="clickable-tab">
              <Nav.Link eventKey="loads" className="d-flex align-items-center py-2 px-3">
                <Truck size={14} className="me-1" />
                <span className="small">Loads</span>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item onClick={() => setActiveTab('sites')} className="clickable-tab">
              <Nav.Link eventKey="sites" className="d-flex align-items-center py-2 px-3">
                <MapPin size={14} className="me-1" />
                <span className="small">Sites</span>
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          <Tab.Content>
              <Tab.Pane eventKey="summary" active={activeTab === 'summary'}>
                <div className="row">
                  {/* Contact Cards - 2x2 Grid */}
                  <div className="col-12 mb-4">
                    <div className="row mb-4 g-3">
                      {/* Top Row */}
                      <div className="col-12 col-sm-6 col-md-6 mb-3 mb-md-4">
                        <Card className="h-100">
                          <Card.Header className="bg-primary text-white">
                            <h6 className="mb-0 d-flex align-items-center">
                              <User size={16} className="me-2" />
                              Project Owner
                            </h6>
                          </Card.Header>
                          <Card.Body>
                            <div className="mb-2">
                              <strong>Name:</strong> {displayValue(project.projectOwner?.name)}
                            </div>
                            <div className="mb-2">
                              <strong>Email:</strong> {renderEmailLink(project.projectOwner?.email)}
                            </div>
                            <div className="mb-0">
                              <strong>Phone:</strong> {displayValue(project.projectOwner?.phone)}
                            </div>
                          </Card.Body>
                        </Card>
                      </div>
                      <div className="col-12 col-sm-6 col-md-6 mb-3 mb-md-4">
                        <Card className="h-100">
                          <Card.Header className="bg-success text-white">
                            <h6 className="mb-0 d-flex align-items-center">
                              <User size={16} className="me-2" />
                              Salesperson
                            </h6>
                          </Card.Header>
                          <Card.Body>
                            <div className="mb-2">
                              <strong>Name:</strong> {displayValue(project.salesperson?.name)}
                            </div>
                            <div className="mb-2">
                              <strong>Email:</strong> {renderEmailLink(project.salesperson?.email)}
                            </div>
                            <div className="mb-0">
                              <strong>Phone:</strong> {displayValue(project.salesperson?.phone)}
                            </div>
                          </Card.Body>
                        </Card>
                      </div>
                      {/* Bottom Row - Project Manager */}
                      <div className="col-12 col-sm-6 col-md-6 mb-3 mb-md-4">
                        <Card className="h-100">
                          <Card.Header className="bg-info text-white">
                            <h6 className="mb-0 d-flex align-items-center">
                              <User size={16} className="me-2" />
                              Project Manager
                            </h6>
                          </Card.Header>
                          <Card.Body>
                            <div className="row">
                              {/* Left Column - Company PM */}
                              <div className="col-12 col-md-6 project-manager-column pe-md-3">
                                <div className="mb-3 d-flex justify-content-between align-items-center">
                                  <h6 className="mb-0 fw-bold">{company?.name || 'Company'}</h6>
                                  {!isEditingCompanyPM && (
                                    <Button
                                      variant="link"
                                      className="p-0 text-decoration-none"
                                      onClick={handleEditCompanyPM}
                                      title="Edit company PM"
                                    >
                                      <Edit size={14} />
                                    </Button>
                                  )}
                                </div>
                                {isEditingCompanyPM ? (
                                  <div>
                                    <div className="mb-3">
                                      <Form.Label className="small fw-semibold">Name:</Form.Label>
                                      <Form.Control
                                        type="text"
                                        value={companyPMForm.name}
                                        onChange={(e) => setCompanyPMForm(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder="Enter name"
                                        size="sm"
                                      />
                                    </div>
                                    <div className="mb-3">
                                      <Form.Label className="small fw-semibold">Email:</Form.Label>
                                      <Form.Control
                                        type="email"
                                        value={companyPMForm.email}
                                        onChange={(e) => setCompanyPMForm(prev => ({ ...prev, email: e.target.value }))}
                                        placeholder="Enter email"
                                        size="sm"
                                      />
                                    </div>
                                    <div className="mb-3">
                                      <Form.Label className="small fw-semibold">Phone:</Form.Label>
                                      <Form.Control
                                        type="tel"
                                        value={companyPMForm.phone}
                                        onChange={(e) => setCompanyPMForm(prev => ({ ...prev, phone: e.target.value }))}
                                        placeholder="Enter phone"
                                        size="sm"
                                      />
                                    </div>
                                    <div className="d-flex gap-2">
                                      <Button
                                        variant="success"
                                        size="sm"
                                        onClick={handleSaveCompanyPM}
                                        disabled={isSavingCompanyPM}
                                      >
                                        {isSavingCompanyPM ? (
                                          <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Saving...
                                          </>
                                        ) : (
                                          <>
                                            <Save size={14} className="me-1" />
                                            Save
                                          </>
                                        )}
                                      </Button>
                                      <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={handleCancelEditCompanyPM}
                                        disabled={isSavingCompanyPM}
                                      >
                                        <X size={14} className="me-1" />
                                        Cancel
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <div className="mb-2">
                                      <strong>Name:</strong> {displayValue(project.custPMName) || ' '}
                                    </div>
                                    <div className="mb-2">
                                      <strong>Email:</strong> {renderEmailLink(project.custPMEmail) || ' '}
                                    </div>
                                    <div className="mb-0">
                                      <strong>Phone:</strong> {displayValue(project.custPMPhone) || ' '}
                                    </div>
                                  </>
                                )}
                              </div>
                              {/* Right Column - Tyler PM */}
                              <div className="col-12 col-md-6 mt-3 mt-md-0">
                                <div className="mb-3">
                                  <h6 className="mb-2 fw-bold">Tyler Reinforcing</h6>
                                </div>
                                <div className="mb-2">
                                  <strong>Name:</strong> {displayValue(project.projectManager?.name)}
                                </div>
                                <div className="mb-2">
                                  <strong>Email:</strong> {renderEmailLink(project.projectManager?.email)}
                                </div>
                                <div className="mb-0">
                                  <strong>Phone:</strong> {displayValue(project.projectManager?.phone)}
                                </div>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </div>
                      <div className="col-12 col-sm-6 col-md-6 mb-3 mb-md-4">
                        <Card className="h-100">
                          <Card.Header className="bg-warning text-dark">
                            <h6 className="mb-0 d-flex align-items-center justify-content-between">
                              <span className="d-flex align-items-center">
                                <User size={16} className="me-2" />
                                Site Contact
                              </span>
                              {!isEditingSiteContact && (
                                <button
                                  className="btn btn-link text-dark p-0 border-0 bg-transparent"
                                  onClick={handleEditSiteContact}
                                  style={{ textDecoration: 'none', cursor: 'pointer' }}
                                  title="Edit site contact"
                                >
                                  <Edit size={16} />
                                </button>
                              )}
                            </h6>
                          </Card.Header>
                          <Card.Body>
                            {isEditingSiteContact ? (
                              <div>
                                <div className="mb-3">
                                  <Form.Label className="small fw-semibold">Name:</Form.Label>
                                  <Form.Control
                                    type="text"
                                    value={siteContactForm.name}
                                    onChange={(e) => setSiteContactForm(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="Enter name"
                                    size="sm"
                                  />
                                </div>
                                <div className="mb-3">
                                  <Form.Label className="small fw-semibold">Email:</Form.Label>
                                  <Form.Control
                                    type="email"
                                    value={siteContactForm.email}
                                    onChange={(e) => setSiteContactForm(prev => ({ ...prev, email: e.target.value }))}
                                    placeholder="Enter email"
                                    size="sm"
                                  />
                                </div>
                                <div className="mb-3">
                                  <Form.Label className="small fw-semibold">Phone:</Form.Label>
                                  <Form.Control
                                    type="tel"
                                    value={siteContactForm.phone}
                                    onChange={(e) => setSiteContactForm(prev => ({ ...prev, phone: e.target.value }))}
                                    placeholder="Enter phone"
                                    size="sm"
                                  />
                                </div>
                                <div className="d-flex gap-2">
                                  <Button
                                    variant="success"
                                    size="sm"
                                    onClick={handleSaveSiteContact}
                                    disabled={isSavingSiteContact}
                                  >
                                    {isSavingSiteContact ? (
                                      <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Saving...
                                      </>
                                    ) : (
                                      <>
                                        <Save size={14} className="me-1" />
                                        Save
                                      </>
                                    )}
                                  </Button>
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={handleCancelEditSiteContact}
                                    disabled={isSavingSiteContact}
                                  >
                                    <X size={14} className="me-1" />
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="mb-2">
                                  <strong>Name:</strong> {displayValue(project.siteContact?.name) || <span className="text-muted">Not provided</span>}
                                </div>
                                <div className="mb-2">
                                  <strong>Email:</strong> {renderEmailLink(project.siteContact?.email) || ' '}
                                </div>
                                <div className="mb-0">
                                  <strong>Phone:</strong> {displayValue(project.siteContact?.phone) || <span className="text-muted">Not provided</span>}
                                </div>
                              </>
                            )}
                          </Card.Body>
                        </Card>
                      </div>
                    </div>
                  </div>

                  {/* Project Summary Card */}
                  <div className="col-12 mb-4">
                    <Card className="h-100">
                      <Card.Header>
                        <h6 className="mb-0">Project Summary</h6>
                      </Card.Header>
                      <Card.Body>
                        <p className="mb-0 small">{project.summary || 'No summary available'}</p>
                      </Card.Body>
                    </Card>
                  </div>
                </div>
              </Tab.Pane>

              <Tab.Pane eventKey="releases" active={activeTab === 'releases'}>
                
                {releasesLoading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading releases...</span>
                    </Spinner>
                    <p className="mt-3 text-muted">Loading releases...</p>
                  </div>
                ) : releasesError ? (
                  <Alert variant="danger">
                    <AlertCircle size={20} className="me-2" />
                    Error loading releases: {releasesError}
                    <Button 
                      variant="outline-danger" 
                      size="sm" 
                      className="ms-3"
                      onClick={fetchReleases}
                    >
                      Try Again
                    </Button>
                  </Alert>
                ) : releases.length === 0 ? (
                  <div className="text-center py-4">
                    <Package size={48} className="text-muted mb-3" />
                    <h5 className="text-muted">No Releases Found</h5>
                    <p className="text-muted">This project doesn't have any releases yet.</p>
                  </div>
                ) : (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">
                        Releases 
                        {filteredReleases.length !== releases.length && (
                          <Badge bg="info" className="ms-2">
                            {filteredReleases.length} of {releases.length}
                          </Badge>
                        )}
                      </h6>
                      <div className="d-flex gap-2">
                        <Button 
                          variant="outline-secondary" 
                          size="sm"
                          onClick={() => setShowFilters(!showFilters)}
                        >
                          <Filter size={16} className="me-2" />
                          Filters
                        </Button>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => navigate(`/company/${companyId}/project/${projectId}/release-report`, { 
                            state: { project, company } 
                          })}
                        >
                          <FileText size={16} className="me-2" />
                          View Report
                        </Button>
                      </div>
                    </div>

                    {/* Filter Panel */}
                    {showFilters && (
                      <Card className="mb-3">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                          <h6 className="mb-0">Filter Releases</h6>
                          <div className="d-flex gap-2">
                            <Button 
                              variant="primary" 
                              size="sm"
                              onClick={applyFiltersToReleases}
                            >
                              Apply Filters
                            </Button>
                            <Button 
                              variant="outline-secondary" 
                              size="sm"
                              onClick={clearFilters}
                            >
                              <RotateCcw size={14} className="me-1" />
                              Clear All
                            </Button>
                          </div>
                        </Card.Header>
                        <Card.Body>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Search</Form.Label>
                                <InputGroup>
                                  <InputGroup.Text>
                                    <Search size={16} />
                                  </InputGroup.Text>
                                  <Form.Control
                                    type="text"
                                    placeholder="Search by release number, description, or account code..."
                                    value={tempFilters.search}
                                    onChange={(e) => updateFilter('search', e.target.value)}
                                  />
                                </InputGroup>
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Status</Form.Label>
                                <Form.Select
                                  value={tempFilters.status}
                                  onChange={(e) => updateFilter('status', e.target.value)}
                                >
                                  <option value="all">All Statuses</option>
                                  <option value="Pending">Pending</option>
                                  <option value="In Transit">{loadStatusDisplayAsStored('In Transit')}</option>
                                  <option value="Delivered">Delivered</option>
                                  <option value="Completed">Completed</option>
                                  <option value="Cancelled">Cancelled</option>
                                </Form.Select>
                              </Form.Group>
                            </Col>
                          </Row>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Date From</Form.Label>
                                <Form.Control
                                  type="date"
                                  value={tempFilters.dateFrom}
                                  onChange={(e) => updateFilter('dateFrom', e.target.value)}
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Date To</Form.Label>
                                <Form.Control
                                  type="date"
                                  value={tempFilters.dateTo}
                                  onChange={(e) => updateFilter('dateTo', e.target.value)}
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Min Weight (lbs)</Form.Label>
                                <Form.Control
                                  type="number"
                                  placeholder="Minimum weight"
                                  value={tempFilters.weightMin}
                                  onChange={(e) => updateFilter('weightMin', e.target.value)}
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>Max Weight (lbs)</Form.Label>
                                <Form.Control
                                  type="number"
                                  placeholder="Maximum weight"
                                  value={tempFilters.weightMax}
                                  onChange={(e) => updateFilter('weightMax', e.target.value)}
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    )}
                    {filteredReleases.length === 0 && releases.length > 0 ? (
                      <div className="text-center py-4">
                        <Search size={48} className="text-muted mb-3" />
                        <h5 className="text-muted">No Releases Match Your Filters</h5>
                        <p className="text-muted">Try adjusting your filter criteria or clear all filters.</p>
                        <Button variant="outline-primary" onClick={clearFilters}>
                          <RotateCcw size={16} className="me-2" />
                          Clear Filters
                        </Button>
                      </div>
                    ) : (
                      <Table responsive className="modern-table">
                        <thead>
                          <tr>
                            <th>Release Number</th>
                            <th>Description</th>
                            <th>Release Date</th>
                            <th>Actions</th>
                            <th style={{ textAlign: 'right' }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredReleases.map((release) => (
                            <React.Fragment key={release.id}>
                              <tr 
                                className="release-row-clickable"
                                style={{ cursor: 'pointer' }}
                                onClick={() => toggleReleaseExpansion(release.id)}
                                data-release-id={release.id}
                              >
                                <td>
                                  <div className="d-flex align-items-center">
                                    {expandedReleases.has(release.id) ? (
                                      <ChevronDown size={16} className="me-2" />
                                    ) : (
                                      <ChevronRight size={16} className="me-2" />
                                    )}
                                    <Badge 
                                      bg="light" 
                                      text="dark"
                                      style={{ cursor: 'pointer', color: '#0d6efd' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveTab('releases');
                                        setTimeout(() => {
                                          // Close all other releases and open only this one
                                          expandReleaseOnly(release.id);
                                          const releaseElement = document.querySelector(`[data-release-id="${release.id}"]`);
                                          if (releaseElement) {
                                            releaseElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                          }
                                        }, 100);
                                      }}
                                    >
                                      {release.releaseNumber}
                                    </Badge>
                                  </div>
                                </td>
                                <td>{release.description}</td>
                                <td>{new Date(release.releaseDate).toLocaleDateString()}</td>
                                <td onClick={(e) => e.stopPropagation()}>
                                  {(() => {
                                    // Get user's company ID
                                    const userCompanyId = user?.assetId || user?.company?.id;
                                    // Check if user's company ID matches placer company
                                    const isUserPlacer = project?.placerCompany && userCompanyId && (
                                      String(project.placerCompany).trim() === String(userCompanyId).trim() ||
                                      project.placerCompany.toString().split(',').map(c => c.trim()).includes(String(userCompanyId).trim())
                                    );
                                    
                                    // Check IncludePAsInCost and SacCode logic for 100% Placed button
                                    const includePAsInCost = project?.includePAsInCost === 1;
                                    const sacCode = release.sacCode || '';
                                    const isPA = sacCode === 'PA';
                                    const shouldShow100PercentButton = includePAsInCost || !isPA;
                                    
                                    // Check if user is placer
                                    const userCompanyIsPlacer = user?.company?.isPlacer === 1;
                                    const isUserPlacerForButton = userCompanyIsPlacer && project?.placerCompany && userCompanyId && (
                                      String(project.placerCompany).trim() === String(userCompanyId).trim() ||
                                      project.placerCompany.toString().split(',').map(c => c.trim()).includes(String(userCompanyId).trim())
                                    );
                                    
                                    // Only show button if user is placer, release is in transit (API may still say shipped), and conditions are met
                                    const st = release.status?.toLowerCase() ?? '';
                                    return (st === 'intransit' || st === 'shipped') && release.weight && release.weight > 0 && isUserPlacerForButton && shouldShow100PercentButton && (
                                      <Button
                                        variant="outline-success"
                                        size="sm"
                                        onClick={async () => {
                                          try {
                                            const totalWeight = Math.round(release.weight);
                                            const today = new Date().toISOString().split('T')[0];
                                            
                                            await apiService.updateReleaseWeight(release.id, {
                                              placedWt: totalWeight,
                                              placedDate: today
                                            });
                                            
                                            const updatedRelease = {
                                              ...release,
                                              placedWt: totalWeight,
                                              placedDate: today,
                                              status: 'Placed'
                                            };
                                            
                                            handleReleaseUpdate(updatedRelease);
                                            showToastNotification(`Weight updated to 100% (${totalWeight} lbs)`, 'success');
                                          } catch (error) {
                                            console.error('Error updating weight to 100%:', error);
                                            showToastNotification('Error updating weight. Please try again.', 'error');
                                          }
                                        }}
                                      >
                                        100% Placed
                                      </Button>
                                    );
                                  })()}
                                </td>
                                <td style={{ textAlign: 'right' }}>{getStatusBadge(release.status)}</td>
                              </tr>
                              {/* Release Details Dropdown Row */}
                              {expandedReleases.has(release.id) && (
                                <tr className="release-details-row">
                                  <td colSpan={6} className="p-0">
                                    <ReleaseDetailsCard 
                                      release={release} 
                                      onReleaseUpdate={handleReleaseUpdate}
                                      loads={loads}
                                      companyId={companyId}
                                      projectId={projectId}
                                      placerCompany={project?.placerCompany}
                                      project={project}
                                      onSwitchToLoads={(loadId) => {
                                        setActiveTab('loads');
                                        setTimeout(() => {
                                          // Close all other loads and open only this one
                                          expandLoadOnly(loadId);
                                          // Scroll to the load
                                          const loadElement = document.querySelector(`[data-load-id="${loadId}"]`);
                                          if (loadElement) {
                                            loadElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                          }
                                        }, 100);
                                      }}
                                    />
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </>
                )}
              </Tab.Pane>

              <Tab.Pane eventKey="loads" active={activeTab === 'loads'}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Project Loads</h6>
                </div>
                
                {loadsLoading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading loads...</span>
                    </Spinner>
                    <p className="mt-3 text-muted">Loading loads...</p>
                  </div>
                ) : loadsError ? (
                  <Alert variant="danger">
                    <AlertCircle size={20} className="me-2" />
                    Error loading loads: {loadsError}
                    <Button 
                      variant="outline-danger" 
                      size="sm" 
                      className="ms-3"
                      onClick={fetchLoads}
                    >
                      Try Again
                    </Button>
                  </Alert>
                ) : loads.length === 0 ? (
                  <div className="text-center py-4">
                    <Truck size={48} className="text-muted mb-3" />
                    <h5 className="text-muted">No Loads Found</h5>
                    <p className="text-muted">This project doesn't have any loads yet.</p>
                  </div>
                ) : (
                  <Table responsive className="modern-table">
                    <thead>
                      <tr>
                        <th>Load Number</th>
                        <th>Load Code</th>
                        <th>Delivery Date</th>
                        <th style={{ textAlign: 'right' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loads.map((load) => (
                        <React.Fragment key={load.id}>
                          <tr 
                            className="load-row"
                            style={{ cursor: 'pointer' }}
                            onClick={() => toggleLoadExpansion(load.id)}
                            data-load-id={load.id}
                          >
                            <td>
                              <div className="d-flex align-items-center">
                                {expandedLoads.has(load.id) ? (
                                  <ChevronDown size={16} className="me-2" />
                                ) : (
                                  <ChevronRight size={16} className="me-2" />
                                )}
                                <Badge bg="light" text="dark" style={{ fontWeight: 'bold' }}>
                                  B{load.loadNumber}
                                </Badge>
                              </div>
                            </td>
                            <td>{load.loadDesignator ? convertLoadDesignatorToLetters(load.loadDesignator) : ''}</td>
                            <td>{load.shippedDate ? new Date(load.shippedDate).toLocaleDateString() : (load.loadDate ? new Date(load.loadDate).toLocaleDateString() : '')}</td>
                            <td style={{ textAlign: 'right' }}>{renderLoadWorkflowStatusBadge(load.status)}</td>
                          </tr>
                          {/* Collapsible Load Details Row */}
                          {expandedLoads.has(load.id) && (
                            <tr>
                              <td colSpan={4} className="p-0">
                                <LoadDetailsSection 
                                  load={load} 
                                  releases={loadReleases[load.id] || []} 
                                  companyId={companyId}
                                  projectId={projectId}
                                  project={project}
                                  user={user}
                                  onReleaseClick={(release) => {
                                    // Close all other loads and keep only the current load open
                                    expandLoadOnly(load.id);
                                    // Don't show modal when coming from loads page
                                    // Just navigate to releases tab and expand
                                    setActiveTab('releases');
                                    setTimeout(() => {
                                      // Close all other releases and open only this one
                                      expandReleaseOnly(release.id);
                                      // Scroll to the release
                                      const releaseElement = document.querySelector(`[data-release-id="${release.id}"]`);
                                      if (releaseElement) {
                                        releaseElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                      }
                                    }, 100);
                                  }}
                                  onNavigateToRelease={(releaseId) => {
                                    // Close all other loads and keep only the current load open
                                    expandLoadOnly(load.id);
                                    // Navigate to releases tab and expand the release
                                    setActiveTab('releases');
                                    setTimeout(() => {
                                      // Close all other releases and open only this one
                                      expandReleaseOnly(releaseId);
                                      // Scroll to the release
                                      const releaseElement = document.querySelector(`[data-release-id="${releaseId}"]`);
                                      if (releaseElement) {
                                        releaseElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                      }
                                    }, 100);
                                  }}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Tab.Pane>

              <Tab.Pane eventKey="sites" active={activeTab === 'sites'}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Project Sites</h6>
                  <Button variant="outline-primary" size="sm" onClick={handleAddSite}>
                    <MapPin size={16} className="me-2" />
                    Add Site
                  </Button>
                </div>
                
                {/* Two-column layout: Sites list on left, Map on right */}
                <div className="row g-3">
                  {/* Left Column: Sites List */}
                  <div className="col-12 col-md-6">
                    {sitesLoading ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2 text-muted">Loading project sites...</p>
                      </div>
                    ) : sitesError ? (
                      <Alert variant="danger">
                        <AlertCircle size={20} className="me-2" />
                        Error loading sites: {sitesError}
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="ms-2"
                          onClick={fetchSites}
                        >
                          Retry
                        </Button>
                      </Alert>
                    ) : sites.length === 0 ? (
                      <div className="text-center py-5">
                        <MapPin size={48} className="text-muted mb-3" />
                        <h5 className="text-muted">No Sites Found</h5>
                        <p className="text-muted">This project doesn't have any sites defined yet.</p>
                        <Button variant="primary" size="sm" onClick={handleAddSite}>
                          <MapPin size={16} className="me-2" />
                          Add First Site
                        </Button>
                      </div>
                    ) : (
                      <div>
                        {/* Sites List - Single Column */}
                        <div className="mb-3">
                          {sites.map((site) => {
                            const isExpanded = expandedSiteCards.has(site.id);
                            return (
                              <Card key={site.id} className="mb-3">
                                <Card.Header
                                  className="bg-light clickable-header"
                                  onClick={() => toggleSiteCard(site.id)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0">
                                      {site.siteAnnotation ? `Site ${site.siteAnnotation}` : 'Project site'}
                                    </h6>
                                    <div className="d-flex align-items-center gap-2">
                                      <Badge bg="secondary" className="small">
                                        {site.latitude && site.longitude ? 'Located' : 'No Location'}
                                      </Badge>
                                      <ChevronDown
                                        size={16}
                                        className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                        style={{
                                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                          transition: 'transform 0.2s ease'
                                        }}
                                      />
                                    </div>
                                  </div>
                                </Card.Header>
                                {isExpanded && (
                                  <Card.Body>
                                    <div className="mb-3 d-flex justify-content-between align-items-start gap-3">
                                      <div className="flex-grow-1">
                                        <h6 className="text-muted small mb-1">Description</h6>
                                        <p className="mb-0">{site.description || 'No description provided'}</p>
                                      </div>
                                      {site.latitude && site.longitude && (
                                        <Button
                                          variant="outline-success"
                                          size="sm"
                                          onClick={() => {
                                            setSelectedSite(site);
                                            setShowSendLocationModal(true);
                                          }}
                                          className="flex-shrink-0"
                                        >
                                          <Mail size={14} className="me-2" />
                                          Send Location
                                        </Button>
                                      )}
                                    </div>

                                    {site.latitude && site.longitude && (
                                      <div className="mb-3">
                                        <h6 className="text-muted small mb-1">Location</h6>
                                        <div className="bg-light p-2 rounded">
                                          <small className="text-muted">
                                            Lat: {site.latitude.toFixed(6)}<br />
                                            Lng: {site.longitude.toFixed(6)}
                                          </small>
                                        </div>
                                      </div>
                                    )}

                                    <div className="mt-3 d-flex gap-2">
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="flex-fill"
                                        onClick={() => handleEditSite(site)}
                                      >
                                        <Edit size={14} className="me-2" />
                                        Edit
                                      </Button>
                                      {site.latitude && site.longitude && (
                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          className="flex-fill"
                                          onClick={() => {
                                            const mapUrl = `https://www.google.com/maps?q=${site.latitude},${site.longitude}&z=15`;
                                            window.open(mapUrl, '_blank');
                                          }}
                                        >
                                          <MapPin size={14} className="me-2" />
                                          View on Map
                                        </Button>
                                      )}
                                    </div>
                                  </Card.Body>
                                )}
                              </Card>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Column: Map */}
                  <div className="col-12 col-md-6">
                    <div style={{
                      width: '100%',
                      aspectRatio: '1.15',
                      position: 'relative'
                    }}>
                      <div
                        ref={sitesMapContainerRef}
                        id="sites-map"
                        style={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          border: '1px solid #e5e7eb',
                          position: 'absolute',
                          top: 0,
                          left: 0
                        }}
                      />
                    </div>
                  </div>
                </div>
              </Tab.Pane>
            </Tab.Content>
        </div>


      {/* Edit Project Modal */}
      <Modal show={showEditModal} onHide={handleCancelEdit} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Project Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.projectName}
                    onChange={(e) => setEditForm(prev => ({ ...prev, projectName: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Project Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.custProjNo}
                    onChange={(e) => setEditForm(prev => ({ ...prev, custProjNo: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tyler Project Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.tylerProjectName}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tyler Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.tylerNumber}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Contact Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.contactName}
                    onChange={(e) => setEditForm(prev => ({ ...prev, contactName: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Primary Site</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.primarySite}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Contact Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={editForm.contactEmail}
                    onChange={(e) => setEditForm(prev => ({ ...prev, contactEmail: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Contact Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    value={editForm.contactPhone}
                    onChange={(e) => setEditForm(prev => ({ ...prev, contactPhone: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Summary</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={editForm.summary}
                readOnly
                style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelEdit}>
            <X size={16} className="me-2" />
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveProject}>
            <Save size={16} className="me-2" />
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Site Modal */}
      <Modal show={showAddSiteModal} onHide={handleCancelAddSite} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingSite ? 'Edit Project Site' : 'Add New Project Site'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* Map Section */}
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Select Location on Map</Form.Label>
                  <div className="position-relative">
                    <InputGroup className="mb-2">
                      <Form.Control
                        type="text"
                        placeholder="Search by address, city, or place name"
                        value={mapSearchQuery}
                        onChange={(e) => setMapSearchQuery(e.target.value)}
                        onFocus={() => setShowSearchHistory(true)}
                        onBlur={() => setTimeout(() => setShowSearchHistory(false), 200)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleMapSearch();
                          }
                        }}
                      />
                      <Button
                        variant="primary"
                        onClick={handleMapSearch}
                        disabled={mapSearchLoading}
                      >
                        {mapSearchLoading ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          'Search'
                        )}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={handleGeolocationSearch}
                        disabled={mapSearchLoading}
                        title="Use current location"
                      >
                        📍
                      </Button>
                    </InputGroup>
                    {mapSearchSuggestionsLoading && (
                      <div className="position-absolute end-0 top-50 translate-middle-y me-3 text-muted small">
                        Searching...
                      </div>
                    )}
                    {mapSearchSuggestions.length > 0 && (
                      <div
                        className="list-group position-absolute w-100 shadow-sm"
                        style={{ zIndex: 1050, maxHeight: '200px', overflowY: 'auto' }}
                      >
                        {mapSearchSuggestions.map((suggestion, idx) => (
                          <button
                            type="button"
                            key={`${suggestion.place_id || idx}`}
                            className="list-group-item list-group-item-action"
                            onClick={() => {
                              applyMapSearchResult(suggestion);
                              setMapSearchSuggestions([]);
                            }}
                          >
                            <div className="fw-semibold small text-start">{suggestion.display_name}</div>
                            {suggestion.address && (
                              <div className="text-muted tiny text-start">
                                {[
                                  suggestion.address.road,
                                  suggestion.address.city || suggestion.address.town || suggestion.address.village,
                                  suggestion.address.state
                                ].filter(Boolean).join(', ')}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                    {showSearchHistory && searchHistory.length > 0 && mapSearchSuggestions.length === 0 && (
                      <div
                        className="list-group position-absolute w-100 shadow-sm"
                        style={{ zIndex: 1049, maxHeight: '200px', overflowY: 'auto' }}
                      >
                        <div className="list-group-item bg-light fw-semibold small d-flex justify-content-between align-items-center">
                          Recent Searches
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={clearSearchHistory}
                            title="Clear history"
                          >
                            🗑️
                          </button>
                        </div>
                        {searchHistory.map((item) => (
                          <button
                            type="button"
                            key={item.id}
                            className="list-group-item list-group-item-action"
                            onClick={() => {
                              setMapSearchQuery(item.query);
                              applyMapSearchResult(item.result);
                              setShowSearchHistory(false);
                            }}
                          >
                            <div className="fw-semibold small text-start">{item.query}</div>
                            <div className="text-muted tiny text-start">
                              {new Date(item.timestamp).toLocaleDateString()}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {mapSearchError && (
                    <div className="text-danger small mb-2">{mapSearchError}</div>
                  )}
                  <div className="border rounded" style={{ height: '300px', position: 'relative' }}>
                    {mapLoaded ? (
                      <div
                        id="site-map"
                        style={{ width: '100%', height: '100%' }}
                      />
                    ) : mapError ? (
                      <div className="d-flex align-items-center justify-content-center h-100">
                        <div className="text-center">
                          <div className="text-warning mb-3">
                            <i className="bi bi-exclamation-triangle" style={{ fontSize: '2rem' }}></i>
                          </div>
                          <h6 className="text-muted">Map Loading Failed</h6>
                          <p className="text-muted small mb-3">Please enter coordinates manually below</p>
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            onClick={() => {
                              setMapError(false);
                              setMapLoaded(false);
                              // Reload the map
                              window.location.reload();
                            }}
                          >
                            Retry Loading Map
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center h-100">
                        <div className="text-center">
                          <Spinner animation="border" variant="primary" />
                          <p className="mt-2 text-muted">Loading interactive map...</p>
                          <p className="text-muted small">Using OpenStreetMap for reliable location selection</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <Form.Text className="text-muted mb-0">
                      {mapLoaded ? 
                        "Click on the map to select the site location. The coordinates will be automatically filled below." :
                        "Enter the latitude and longitude coordinates manually below."
                      }
                    </Form.Text>
                    {selectedLocation && (
                      <Button 
                        variant="outline-secondary" 
                        size="sm" 
                        onClick={() => {
                          setSelectedLocation(null);
                          setSiteForm(prev => ({
                            ...prev,
                            latitude: '',
                            longitude: ''
                          }));
                        }}
                      >
                        Clear Location
                      </Button>
                    )}
                  </div>
                </Form.Group>
              </Col>
            </Row>

            {/* Description Field */}
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="e.g., Main Building, Parking Lot, or any additional details about this site location"
                value={siteForm.description}
                onChange={(e) => handleSiteFormChange('description', e.target.value)}
              />
            </Form.Group>

            {/* Manual Coordinate Entry (Fallback) */}
            {(!mapLoaded || mapError) && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Latitude</Form.Label>
                    <Form.Control
                      type="number"
                      step="any"
                      placeholder="e.g., 33.597048"
                      value={siteForm.latitude}
                      onChange={(e) => handleSiteFormChange('latitude', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Longitude</Form.Label>
                    <Form.Control
                      type="number"
                      step="any"
                      placeholder="e.g., -111.880515"
                      value={siteForm.longitude}
                      onChange={(e) => handleSiteFormChange('longitude', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            {/* Selected Location Display */}
            {selectedLocation && (
              <Row>
                <Col md={12}>
                  <div className="alert alert-success d-flex align-items-center">
                    <div className="me-3">
                      <i className="bi bi-geo-alt-fill"></i>
                    </div>
                    <div>
                      <strong>Location Selected:</strong> {siteForm.latitude}, {siteForm.longitude}
                    </div>
                  </div>
                </Col>
              </Row>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Orientation</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., North, South, East, West"
                    value={siteForm.orientation}
                    onChange={(e) => handleSiteFormChange('orientation', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Site Contact Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Contact person name"
                    value={siteForm.siteContactName}
                    onChange={(e) => handleSiteFormChange('siteContactName', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Site Contact Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="Phone number"
                    value={siteForm.siteContactPhone}
                    onChange={(e) => handleSiteFormChange('siteContactPhone', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Site Contact Email</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Email address"
                    value={siteForm.siteContactEmail}
                    onChange={(e) => handleSiteFormChange('siteContactEmail', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Additional Map Instructions</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Any special instructions for finding this site..."
                value={siteForm.addlMapInstructions}
                onChange={(e) => handleSiteFormChange('addlMapInstructions', e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelAddSite}>
            <X size={16} className="me-2" />
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveSite} disabled={loading}>
            <Save size={16} className="me-2" />
            {loading ? 'Saving...' : (editingSite ? 'Update Site' : 'Add Site')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Release Details Modal */}
      <Modal show={showReleaseDetailsModal} onHide={() => setShowReleaseDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <Package size={20} className="me-2" />
            Release Details - {selectedRelease?.releaseNumber}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRelease && (
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <Card className="mb-3">
                  <Card.Header>
                    <h6 className="mb-0">Basic Information</h6>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-2">
                      <strong>Release Number:</strong>
                      <div className="text-muted">{displayValue(selectedRelease.releaseNumber)}</div>
                    </div>
                    <div className="mb-2">
                      <strong>Description:</strong>
                      <div className="text-muted">{displayValue(selectedRelease.description)}</div>
                    </div>
                    <div className="mb-2">
                      <strong>Status:</strong>
                      <div>{getStatusBadge(selectedRelease.status)}</div>
                    </div>
                    {(() => {
                      // Check if should hide weight (customer + ContractType != 'Unit Price per Lb')
                      const userCompanyId = user?.assetId || user?.company?.id;
                      const userCompanyIsCustomer = user?.company?.isCustomer === 1;
                      const isCustomer = userCompanyIsCustomer && project?.companyId && userCompanyId && 
                        String(project.companyId).trim() === String(userCompanyId).trim();
                      const contractType = project?.contractType || '';
                      const shouldHideWeightForCustomer = isCustomer && contractType !== 'Unit Price per Lb';
                      return !shouldHideWeightForCustomer ? (
                        <div className="mb-2">
                          <strong>Weight:</strong>
                          <div className="text-muted">{selectedRelease.weight || ''} lbs</div>
                        </div>
                      ) : null;
                    })()}
                  </Card.Body>
                </Card>

                <Card className="mb-3">
                  <Card.Header>
                    <h6 className="mb-0">Project Information</h6>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-2">
                      <strong>Project ID:</strong>
                      <div className="text-muted">{displayValue(selectedRelease.projectId)}</div>
                    </div>
                    <div className="mb-2">
                      <strong>Load ID:</strong>
                      <div className="text-muted">{displayValue(selectedRelease.loadId)}</div>
                    </div>
                    <div className="mb-2">
                      <strong>Bid Item:</strong>
                      <div className="text-muted">{displayValue(selectedRelease.bidItem)}</div>
                    </div>
                  </Card.Body>
                </Card>
              </div>

              <div className="col-12 col-md-6">
                <Card className="mb-3">
                  <Card.Header>
                    <h6 className="mb-0">Codes</h6>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-2">
                      <strong>Account Code:</strong>
                      <div className="text-muted">{displayValue(selectedRelease.acctCode)}</div>
                    </div>
                    <div className="mb-2">
                      <strong>SAC Code:</strong>
                      <div className="text-muted">{displayValue(selectedRelease.sacCode)}</div>
                    </div>
                  </Card.Body>
                </Card>

                <Card className="mb-3">
                  <Card.Header>
                    <h6 className="mb-0">Dates</h6>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-2">
                      <strong>Release Date:</strong>
                      <div className="text-muted">
                        {selectedRelease.releaseDate ? new Date(selectedRelease.releaseDate).toLocaleDateString() : ''}
                      </div>
                    </div>
                    <div className="mb-2">
                      <strong>Firmed Date:</strong>
                      <div className="text-muted">
                        {selectedRelease.firmedDate ? new Date(selectedRelease.firmedDate).toLocaleDateString() : ''}
                      </div>
                    </div>
                    <div className="mb-2">
                      <strong>Tagged Date:</strong>
                      <div className="text-muted">
                        {selectedRelease.taggedDate ? new Date(selectedRelease.taggedDate).toLocaleDateString() : ''}
                      </div>
                    </div>
                    <div className="mb-2">
                      <strong>Fabricated Date:</strong>
                      <div className="text-muted">
                        {selectedRelease.fabricatedDate ? new Date(selectedRelease.fabricatedDate).toLocaleDateString() : ''}
                      </div>
                    </div>
                    <div className="mb-2">
                      <strong>Placed Date:</strong>
                      <div className="text-muted">
                        {selectedRelease.placedDate ? new Date(selectedRelease.placedDate).toLocaleDateString() : ''}
                      </div>
                    </div>
                  </Card.Body>
                </Card>

                <Card className="mb-3">
                  <Card.Header>
                    <h6 className="mb-0">Additional Information</h6>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-2">
                      <strong>Released By:</strong>
                      <div className="text-muted">
                        {selectedRelease.releasedByUser ?
                          `${selectedRelease.releasedByUser.firstName || ''} ${selectedRelease.releasedByUser.lastName || ''}`.trim() || 'Unknown User' :
                          displayValue(selectedRelease.releasedBy)
                        }
                      </div>
                    </div>
                    {(() => {
                      // Check if should hide weight (customer + ContractType != 'Unit Price per Lb')
                      const userCompanyId = user?.assetId || user?.company?.id;
                      const userCompanyIsCustomer = user?.company?.isCustomer === 1;
                      const isCustomer = userCompanyIsCustomer && project?.companyId && userCompanyId && 
                        String(project.companyId).trim() === String(userCompanyId).trim();
                      const contractType = project?.contractType || '';
                      const shouldHideWeightForCustomer = isCustomer && contractType !== 'Unit Price per Lb';
                      return !shouldHideWeightForCustomer ? (
                        <div className="mb-2">
                          <strong>Placed Weight:</strong>
                          <div className="text-muted">{selectedRelease.placedWt || ''} lbs</div>
                        </div>
                      ) : null;
                    })()}
                  </Card.Body>
                </Card>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReleaseDetailsModal(false)}>
            <X size={16} className="me-2" />
            Close
          </Button>
          <Button 
            variant="primary" 
            onClick={() => {
              exportSingleReleasePDF(selectedRelease);
              setShowReleaseDetailsModal(false);
            }}
          >
            <Download size={16} className="me-2" />
            Export PDF
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Send Location Email Modal */}
      <Modal show={showSendLocationModal} onHide={() => {
        setShowSendLocationModal(false);
        setSelectedSite(null);
        setEmailError(null);
        setEmailSuccess(null);
        setEmailFormData({
          configurationId: '',
          receiver: '',
          cc: '',
          bcc: '',
          subject: '',
          body: ''
        });
        setEmailAttachments([]);
        setReceiverQuery('');
        setReceiverSuggestions([]);
        setShowReceiverSuggestions(false);
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <Mail size={20} className="me-2" />
            Send Site Location via Email
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {emailError && (
            <Alert variant="danger" dismissible onClose={() => setEmailError(null)}>
              {emailError}
            </Alert>
          )}
          {emailSuccess && (
            <Alert variant="success" dismissible onClose={() => setEmailSuccess(null)}>
              {emailSuccess}
            </Alert>
          )}

          {selectedSite && (
            <div className="mb-3 p-3 bg-light rounded">
              <h6 className="mb-2">Site Information:</h6>
              <p className="mb-1"><strong>Site:</strong> {selectedSite.siteAnnotation ? `Site ${selectedSite.siteAnnotation}` : 'Project site'}</p>
              <p className="mb-1"><strong>Description:</strong> {selectedSite.description || 'No description'}</p>
              {selectedSite.latitude && selectedSite.longitude && (
                <>
                  <p className="mb-1"><strong>Coordinates:</strong> {selectedSite.latitude.toFixed(6)}, {selectedSite.longitude.toFixed(6)}</p>
                  <p className="mb-0"><strong>Map Link:</strong> <a href={`https://www.google.com/maps?q=${selectedSite.latitude},${selectedSite.longitude}&z=15`} target="_blank" rel="noopener noreferrer">View on Google Maps</a></p>
                </>
              )}
            </div>
          )}

          <Form onSubmit={async (e) => {
            e.preventDefault();
            setSendingEmail(true);
            setEmailError(null);
            setEmailSuccess(null);

            try {
              if (!emailFormData.receiver || !emailFormData.subject || !emailFormData.body) {
                throw new Error('Please fill in all required fields: Receiver, Subject, and Body');
              }

              let attachmentsPayload;
              if (emailAttachments.length > 0) {
                attachmentsPayload = await filesToEmailAttachmentPayloads(emailAttachments);
              }

              const emailData = {
                configurationId: null, // Use default configuration
                receiver: emailFormData.receiver,
                cc: emailFormData.cc || undefined,
                bcc: emailFormData.bcc || undefined,
                subject: emailFormData.subject,
                body: emailFormData.body,
                bodyType: 'html',
                ...(attachmentsPayload?.length ? { attachments: attachmentsPayload } : {})
              };

              const response = await sendEmailService.sendEmail(emailData);

              if (response.status) {
                setEmailSuccess('Email sent successfully!');
                setTimeout(() => {
                  setShowSendLocationModal(false);
                  setSelectedSite(null);
                  setEmailFormData({
                    configurationId: '',
                    receiver: '',
                    cc: '',
                    bcc: '',
                    subject: '',
                    body: ''
                  });
                  setEmailAttachments([]);
                  setReceiverQuery('');
                  setReceiverSuggestions([]);
                  setShowReceiverSuggestions(false);
                  setEmailSuccess(null);
                }, 2000);
              }
            } catch (err) {
              setEmailError(err.message || 'Failed to send email');
            } finally {
              setSendingEmail(false);
            }
          }}>
            <Form.Group className="mb-3">
              <Form.Label>Receiver (To) *</Form.Label>
              <div className="position-relative">
                <Form.Control
                  ref={receiverInputRef}
                  type="text"
                  name="receiver"
                  value={receiverQuery || emailFormData.receiver}
                  onChange={async (e) => {
                    const value = e.target.value;
                    setReceiverQuery(value);
                    setEmailFormData(prev => ({ ...prev, receiver: value }));
                    setEmailError(null);
                    setEmailSuccess(null);

                    const lastPart = value.split(',').pop().trim();
                    if (lastPart.length >= 2) {
                      try {
                        const response = await sendEmailService.searchUsers(lastPart);
                        if (response.status && response.data) {
                          setReceiverSuggestions(response.data);
                          setShowReceiverSuggestions(true);
                        }
                      } catch (err) {
                        console.error('Failed to search users:', err);
                        setReceiverSuggestions([]);
                      }
                    } else {
                      setReceiverSuggestions([]);
                      setShowReceiverSuggestions(false);
                    }
                  }}
                  placeholder="Type name or email to search users, or enter email manually (comma-separated for multiple)"
                  required
                  autoComplete="off"
                />
                {showReceiverSuggestions && receiverSuggestions.length > 0 && (
                  <div ref={receiverSuggestionsRef} className="autocomplete-suggestions" style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    backgroundColor: 'white',
                    border: '1px solid #ced4da',
                    borderRadius: '0.25rem',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    {receiverSuggestions.map((user, index) => (
                      <div
                        key={user.id || index}
                        style={{
                          padding: '0.5rem',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                        onClick={() => {
                          const existingRecipients = receiverQuery.split(',').slice(0, -1).join(',').trim();
                          const newReceiver = existingRecipients 
                            ? `${existingRecipients}, ${user.email}`
                            : user.email;
                          setEmailFormData(prev => ({ ...prev, receiver: newReceiver }));
                          setReceiverQuery(newReceiver);
                          setShowReceiverSuggestions(false);
                        }}
                      >
                        <div style={{ fontWeight: 500 }}>{user.display || user.name}</div>
                        {user.email && user.display && (
                          <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>{user.email}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Form.Text className="text-muted">
                Start typing to search users by name or email. You can add multiple recipients separated by commas.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>CC (Carbon Copy)</Form.Label>
              <Form.Control
                type="text"
                name="cc"
                value={emailFormData.cc}
                onChange={(e) => setEmailFormData(prev => ({ ...prev, cc: e.target.value }))}
                placeholder="email@example.com (comma-separated for multiple)"
              />
              <Form.Text className="text-muted">
                Optional: Enter email address(es) for CC, separated by commas
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>BCC (Blind Carbon Copy)</Form.Label>
              <Form.Control
                type="text"
                name="bcc"
                value={emailFormData.bcc}
                onChange={(e) => setEmailFormData(prev => ({ ...prev, bcc: e.target.value }))}
                placeholder="email@example.com (comma-separated for multiple)"
              />
              <Form.Text className="text-muted">
                Optional: Enter email address(es) for BCC, separated by commas
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Attachments</Form.Label>
              <input
                ref={emailAttachmentInputRef}
                type="file"
                className="d-none"
                multiple
                onChange={(e) => {
                  const picked = e.target.files;
                  if (!picked?.length) return;
                  const merged = [...emailAttachments, ...Array.from(picked)];
                  const v = validateFilesForEmail(merged);
                  if (!v.ok) {
                    setEmailError(v.error);
                    e.target.value = '';
                    return;
                  }
                  setEmailAttachments(v.files);
                  setEmailError(null);
                  e.target.value = '';
                }}
              />
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <Button
                  type="button"
                  variant="outline-secondary"
                  size="sm"
                  disabled={sendingEmail}
                  onClick={() => emailAttachmentInputRef.current?.click()}
                >
                  Add files
                </Button>
                <Form.Text className="text-muted mb-0">
                  Up to {EMAIL_ATTACHMENT_MAX_FILES} files, {EMAIL_ATTACHMENT_MAX_TOTAL_BYTES / (1024 * 1024)} MB total
                </Form.Text>
              </div>
              {emailAttachments.length > 0 && (
                <ul className="list-unstyled small mb-0 border rounded p-2 bg-light">
                  {emailAttachments.map((file, idx) => (
                    <li key={`${file.name}-${idx}`} className="d-flex justify-content-between align-items-center py-1">
                      <span className="text-truncate me-2" title={file.name}>
                        {file.name} ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="text-danger p-0 flex-shrink-0"
                        disabled={sendingEmail}
                        onClick={() => setEmailAttachments((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Subject *</Form.Label>
              <Form.Control
                type="text"
                name="subject"
                value={emailFormData.subject}
                onChange={(e) => setEmailFormData(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Email subject"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Body (Message) *</Form.Label>
              <Form.Control
                as="textarea"
                rows={10}
                name="body"
                value={emailFormData.body}
                onChange={(e) => setEmailFormData(prev => ({ ...prev, body: e.target.value }))}
                placeholder="Enter your email message here..."
                required
              />
              <Form.Text className="text-muted">
                HTML is supported in the email body
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowSendLocationModal(false);
                  setSelectedSite(null);
                  setEmailFormData({
                    configurationId: '',
                    receiver: '',
                    cc: '',
                    bcc: '',
                    subject: '',
                    body: ''
                  });
                  setEmailAttachments([]);
                  setReceiverQuery('');
                  setReceiverSuggestions([]);
                  setShowReceiverSuggestions(false);
                  setEmailError(null);
                  setEmailSuccess(null);
                }}
                disabled={sendingEmail}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={sendingEmail}
                className="d-flex align-items-center gap-2"
              >
                {sendingEmail ? (
                  <>
                    <Spinner size="sm" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Email
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Toast Notification */}
      <ToastContainer position="top-end" className="p-3">
        <Toast show={showToast} onClose={() => setShowToast(false)} delay={4000} autohide className="shadow">
          <Toast.Header closeButton={false} className="bg-white">
            {toastType === 'success' ? (
              <CheckCircle className="text-success me-2" size={20} />
            ) : (
              <AlertCircle className="text-danger me-2" size={20} />
            )}
            <strong className="me-auto">
              {toastType === 'success' ? 'Success' : 'Error'}
            </strong>
          </Toast.Header>
          <Toast.Body className={`bg-white ${toastType === 'success' ? 'text-success' : 'text-danger'}`}>
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
          </>
        )}
      </div>
    </div>
  );
};

export default ProjectDetails;

